import { useState, useCallback, useRef } from "react";

const INR = (v) => "₹" + Math.round(Number(v) || 0).toLocaleString("en-IN");
const PCT = (v, t) => (t > 0 ? Math.round((v / t) * 1000) / 10 : 0) + "%";

// ── CSV Parser ────────────────────────────────────────────────
function parseCSV(text) {
  const lines  = text.trim().split("\n");
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map(h => h.replace(/^"|"$/g, "").trim().toLowerCase());
  return lines.slice(1).map(line => {
    const vals = [];
    let cur = "", inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQ = !inQ; }
      else if (ch === "," && !inQ) { vals.push(cur.trim()); cur = ""; }
      else { cur += ch; }
    }
    vals.push(cur.trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (vals[i] || "").replace(/^'/, ""); });
    return obj;
  });
}

// ── Detect file type ──────────────────────────────────────────
function detectType(rows) {
  if (!rows?.length) return null;
  const keys = Object.keys(rows[0]);
  if (keys.includes("return_reason") || keys.includes("return_id")) return "returns";
  if (keys.includes("final amount") || keys.includes("seller price"))  return "orders";
  return "unknown";
}

// ── Map orders CSV to standard format ────────────────────────
function mapOrders(rows) {
  return rows.map(r => ({
    platform:       "myntra",
    orderId:        r["order release id"] || r["order id fk"] || "",
    orderLineId:    r["order line id"]   || "",
    createdAt:      r["created on"]      || "",
    sku:            r["seller sku code"] || "",
    myntraSku:      r["myntra sku code"] || "",
    productName:    r["style name"]      || "",
    size:           r["size"]            || "",
    status:         r["order status"]    || "",      // C=Delivered F=Fulfilled X/KC=Cancelled
    tracking:       r["order tracking number"] || "",
    courier:        r["courier code"]    || "",
    city:           (r["city"] || "").trim(),
    state:          r["state"]           || "",
    pin:            r["zipcode"]         || "",
    finalAmount:    parseFloat(r["final amount"] || 0),
    totalMRP:       parseFloat(r["total mrp"]    || 0),
    discount:       parseFloat(r["discount"]     || 0),
    couponDiscount: parseFloat(r["coupon discount"] || 0),
    sellerPrice:    parseFloat(r["seller price"] || 0),
    gtCharges:      parseFloat(r["gt charges"]   || 0),
    shippingCharge: parseFloat(r["shipping charge"] || 0),
    deliveredOn:    r["delivered on"]    || "",
    cancelledOn:    r["cancelled on"]    || "",
    returnCreated:  r["return creation date"] || "",
  }));
}

// ── Map returns CSV to standard format ───────────────────────
function mapReturns(rows) {
  return rows.map(r => ({
    platform:       "myntra",
    orderId:        r["order_id"]            || "",
    sku:            r["seller_sku_code"]     || "",
    myntraSku:      r["myntra_sku_code"]     || "",
    returnId:       r["return_id"]           || "",
    returnReason:   r["return_reason"]       || "",
    returnMode:     r["return_mode"]         || "",
    returnStatus:   r["return_status"]       || "",
    isRefunded:     r["is_refunded"] === "1",
    returnCreated:  r["return_created_date"] || "",
    refundedDate:   r["refunded_date"]       || "",
    fwdTracking:    r["forward_tracking_number"] || "",
    retTracking:    r["return_tracking_number"]  || "",
    status:         r["status"]              || "",
    qty:            parseInt(r["quantity"]   || 1),
  }));
}

// ── GST constants ────────────────────────────────────────────
const SELLER_GSTIN    = "09BDOPB0236M2ZF";
const SELLER_NAME     = "Vasudeva Enterprises";
const SELLER_ADDR     = "Tower 4C, Landcraft Golflinks, Pandav Nagar, Ghaziabad, Uttar Pradesh 201001";
const SELLER_STATE    = "UP";
const SELLER_STATE_CD = "09";
const HSN_CODE        = "42033000";
const GST_RATE        = 18; // Belts HSN 42033000 = 18% GST

// State code → name mapping
const STATE_NAMES = {
  AP:"Andhra Pradesh", AR:"Arunachal Pradesh", AS:"Assam", BR:"Bihar", CG:"Chhattisgarh",
  GA:"Goa", GJ:"Gujarat", HR:"Haryana", HP:"Himachal Pradesh", JH:"Jharkhand",
  KA:"Karnataka", KL:"Kerala", MP:"Madhya Pradesh", MH:"Maharashtra", MN:"Manipur",
  ML:"Meghalaya", MZ:"Mizoram", NL:"Nagaland", OD:"Odisha", OR:"Odisha",
  PB:"Punjab", RJ:"Rajasthan", SK:"Sikkim", TN:"Tamil Nadu", TS:"Telangana",
  TR:"Tripura", UP:"Uttar Pradesh", UK:"Uttarakhand", WB:"West Bengal",
  AN:"Andaman & Nicobar", CH:"Chandigarh", DL:"Delhi", JK:"Jammu & Kashmir",
  LA:"Ladakh", LD:"Lakshadweep", PY:"Puducherry", DN:"Dadra & Nagar Haveli",
};

// State GST code mapping
const STATE_CODES = {
  JK:"01", HP:"02", PB:"03", CH:"04", UK:"05", HR:"06", DL:"07", RJ:"08",
  UP:"09", BR:"10", SK:"11", AR:"12", NL:"13", MN:"14", ML:"15", MZ:"16",
  TR:"17", AS:"18", WB:"19", JH:"20", OD:"21", OR:"21", CG:"22", MP:"23",
  GJ:"24", DN:"26", MH:"27", AP:"28", KA:"29", GA:"30", LD:"31", KL:"32",
  TN:"33", PY:"34", AN:"35", TS:"36", LA:"38",
};

const STATUS_MAP = { C:"Delivered", F:"Fulfilled/Cancelled", X:"Cancelled", KC:"Cancelled", MC:"Cancelled", RC:"Return Created" };
const STATUS_COLOR = { C:"#15803d", F:"#d97706", X:"#dc2626", KC:"#dc2626", MC:"#dc2626", RC:"#7e22ce" };

const TABS = [
  { id:"upload",   label:"📁 Upload"         },
  { id:"orders",   label:"🛒 Orders"         },
  { id:"returns",  label:"↩️ Returns"        },
  { id:"finance",  label:"💰 Finance"        },
  { id:"ads",      label:"📢 Ads & P&L"      },
  { id:"metaads",  label:"🎯 Meta Ads Report"},
  { id:"analytics",label:"📊 Analytics"      },
];

const S = {
  card:   { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:12 },
  hdr:    { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", justifyContent:"space-between" },
  th:     { padding:"7px 10px", textAlign:"left", fontSize:10, fontWeight:700, color:"#9ca3af", background:"#f9fafb", whiteSpace:"nowrap" },
  td:     { padding:"7px 10px", fontSize:11, borderBottom:"1px solid #f3f4f6", verticalAlign:"middle" },
  btn:    (c="#F47920") => ({ padding:"7px 14px", borderRadius:7, fontSize:11, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut: { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  kpi:    (color) => ({ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"12px 14px", borderLeft:"3px solid "+color }),
};


export default function PageMyntraImport({ toast, products=[] }) {
  const [tab,        setTab]       = useState("upload");
  const [orders,     setOrders]    = useState(() => { try { return JSON.parse(localStorage.getItem("3june_myntra_orders") || "[]"); } catch { return []; } });
  const [returns,    setReturns]   = useState(() => { try { return JSON.parse(localStorage.getItem("3june_myntra_returns") || "[]"); } catch { return []; } });
  const [uploads,    setUploads]   = useState(() => { try { return JSON.parse(localStorage.getItem("3june_myntra_uploads") || "[]"); } catch { return []; } });
  const [ads,        setAds]        = useState(() => { try { return JSON.parse(localStorage.getItem("3june_myntra_ads") || "[]"); } catch { return []; } });
  const [payRows,    setPayRows]    = useState(() => { try { return JSON.parse(localStorage.getItem("3june_myntra_pay") || "[]"); } catch { return []; } });
  const [settleRows, setSettleRows] = useState(() => { try { return JSON.parse(localStorage.getItem("3june_myntra_settle") || "[]"); } catch { return []; } });
  const [pnlSkus,    setPnlSkus]    = useState(() => { try { return JSON.parse(localStorage.getItem("3june_myntra_pnl") || "[]"); } catch { return []; } });
  const payFileRef    = useRef(null);
  const settleFileRef = useRef(null);
  const [cogDefaults,setCogDefaults]= useState(() => { try { return JSON.parse(localStorage.getItem("3june_myntra_cog") || "{}"); } catch { return {}; } });
  const [additionalCosts,setAdditionalCosts] = useState(() => { try { return JSON.parse(localStorage.getItem("3june_myntra_extra") || "[]"); } catch { return []; } });
  const [showCogForm, setShowCogForm] = useState(false);
  const [showCogUpload, setShowCogUpload] = useState(false);
  const cogExcelRef = useRef(null);
  const [showExtraForm,setShowExtraForm] = useState(false);
  const [cogForm,   setCogForm]  = useState({ costPerUnit:"", rows:[] });
  const [skuSearch, setSkuSearch] = useState("");
  const [myntraSkus,setMyntraSkus]= useState(() => { try { return JSON.parse(localStorage.getItem("3june_myntra_skus")||"[]"); } catch { return []; } });
  const [extraForm,setExtraForm] = useState({ label:"", amount:"", amountType:"per_order", period:"", category:"Packaging" });
  const adFileRef = useRef(null);
  const [uploading,  setUploading] = useState(false);
  const [periodFilter, setPeriodFilter] = useState("all"); // filter all tabs by month
  const [search,     setSearch]    = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const ordRef = useRef(null);
  const retRef = useRef(null);

  // ── Upload handlers ─────────────────────────────────────────
  const handleFile = useCallback(async (file, type) => {
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (!rows.length) { toast("Empty or invalid CSV", "error"); return; }
      const detected = detectType(rows);

      if (detected === "orders" || type === "orders") {
        const mapped   = mapOrders(rows);
        // Merge with existing — deduplicate by orderId
        const existing = JSON.parse(localStorage.getItem("3june_myntra_orders") || "[]");
        const existIds = new Set(existing.map(o => o.orderId));
        const newOnly  = mapped.filter(o => !existIds.has(o.orderId));
        const merged   = [...existing, ...newOnly];
        // Track upload history
        const uploads  = JSON.parse(localStorage.getItem("3june_myntra_uploads") || "[]");
        const period   = mapped[0]?.createdAt?.slice(0,7) || "Unknown";
        uploads.unshift({ type:"orders", file:file.name, period, count:mapped.length, newCount:newOnly.length, date:new Date().toISOString() });
        localStorage.setItem("3june_myntra_orders",  JSON.stringify(merged));
        localStorage.setItem("3june_myntra_uploads", JSON.stringify(uploads.slice(0,50)));
        setOrders(merged);
        setUploads(uploads.slice(0,50));
        toast(`✅ ${newOnly.length} new orders added (${mapped.length-newOnly.length} duplicates skipped) — ${merged.length} total`, "success");
        setTab("orders");

      } else if (detected === "returns" || type === "returns") {
        const mapped   = mapReturns(rows);
        const existing = JSON.parse(localStorage.getItem("3june_myntra_returns") || "[]");
        const existIds = new Set(existing.map(r => r.orderId + r.sku + r.returnCreated));
        const newOnly  = mapped.filter(r => !existIds.has(r.orderId + r.sku + r.returnCreated));
        const merged   = [...existing, ...newOnly];
        const uploads  = JSON.parse(localStorage.getItem("3june_myntra_uploads") || "[]");
        const period   = mapped[0]?.returnCreated?.slice(0,7) || "Unknown";
        uploads.unshift({ type:"returns", file:file.name, period, count:mapped.length, newCount:newOnly.length, date:new Date().toISOString() });
        localStorage.setItem("3june_myntra_returns", JSON.stringify(merged));
        localStorage.setItem("3june_myntra_uploads", JSON.stringify(uploads.slice(0,50)));
        setReturns(merged);
        setUploads(uploads.slice(0,50));
        toast(`✅ ${newOnly.length} new returns added (${mapped.length-newOnly.length} duplicates skipped) — ${merged.length} total`, "success");
        setTab("returns");

      } else if (Object.keys(rows[0]).some(k=>["seller_sku","sku","seller sku","myntra sku","sku_code","parent_sku"].includes(k.toLowerCase().trim()))) {
        // Myntra SKU list
        const skuCol   = Object.keys(rows[0]).find(k=>["seller_sku","sku","seller sku","myntra sku","sku_code","parent_sku"].includes(k.toLowerCase().trim()));
        const nameCol  = Object.keys(rows[0]).find(k=>["product_name","name","style_name","style name","product name"].includes(k.toLowerCase().trim()));
        const mapped = rows.map(r=>({ sku:r[skuCol]||"", name:r[nameCol]||"", source:"SKU List" })).filter(r=>r.sku);
        const deduped = [...new Map(mapped.map(s=>[s.sku,s])).values()];
        localStorage.setItem("3june_myntra_skus", JSON.stringify(deduped));
        setMyntraSkus(deduped);
        const uploads2 = JSON.parse(localStorage.getItem("3june_myntra_uploads")||"[]");
        uploads2.unshift({ type:"skus", file:file.name, period:"", count:parents.length, newCount:parents.length, date:new Date().toISOString() });
        localStorage.setItem("3june_myntra_uploads", JSON.stringify(uploads2.slice(0,50)));
        setUploads(uploads2.slice(0,50));
        toast(`✅ ${deduped.length} SKUs loaded from SKU list`, "success");
        setTab("ads");
      } else if (
        Object.keys(rows[0]).includes("budget_spend") ||
        Object.keys(rows[0]).includes("campaign_id") ||
        Object.keys(rows[0]).some(k => k.toLowerCase().trim() === "campaign name") ||
        Object.keys(rows[0]).some(k => k.toLowerCase().trim() === "spend")
      ) {
        // Ads report — supports both the product-level PLA export (underscore columns)
        // and the campaign-level export (Title Case columns: Campaign Name, Spend, ROI...)
        // Build a case-insensitive lookup so either format works.
        const pick = (r, ...names) => {
          const keys = Object.keys(r);
          for (const n of names) {
            const hit = keys.find(k => k.toLowerCase().trim() === n.toLowerCase().trim());
            if (hit != null && r[hit] !== undefined && r[hit] !== "") return r[hit];
          }
          return "";
        };
        const mapped = rows
          .filter(r => pick(r, "campaign_id", "Campaign ID", "campaign_name", "Campaign Name"))
          .map(r => {
            const spend = parseFloat(pick(r, "budget_spend", "Spend") || 0);
            const unitsTotal = parseInt(pick(r, "units_sold_total", "Units Sold") || 0);
            const revenueTotal = parseFloat(pick(r, "total_revenue", "Revenue") || 0);
            return {
              campaignId:    pick(r, "campaign_id", "Campaign ID") || "",
              campaignName:  pick(r, "campaign_name", "Campaign Name") || "",
              adgroupId:     pick(r, "adgroup_id") || "",
              adgroupName:   pick(r, "adgroup_name") || "",
              targetType:    pick(r, "adgroup_targetting_type") || "",
              productId:     pick(r, "product_id") || "",
              productName:   pick(r, "product_name") || "",
              status:        pick(r, "campaign_status", "Status") || "",
              budget:        parseFloat(pick(r, "Budget", "budget") || 0),
              impressions:   parseInt(pick(r, "impressions", "Impressions") || 0),
              clicks:        parseInt(pick(r, "clicks", "Clicks") || 0),
              ctr:           parseFloat(pick(r, "ctr", "CTR") || 0),
              cvr:           parseFloat(pick(r, "cvr", "CVR") || 0),
              avgCpc:        parseFloat(pick(r, "avg_cpc", "Avg. CPC", "Avg CPC") || 0),
              spend:         spend,
              unitsDirect:   parseInt(pick(r, "units_sold_direct", "Direct - Units Sold") || 0),
              unitsIndirect: parseInt(pick(r, "units_sold_indirect", "Indirect - Units Sold") || 0),
              unitsTotal:    unitsTotal,
              revenueDirect: parseFloat(pick(r, "direct_revenue", "Direct - Revenue") || 0),
              revenueIndirect:parseFloat(pick(r, "indirect_revenue", "Indirect - Revenue") || 0),
              revenueTotal:  revenueTotal,
              roiTotal:      parseFloat(pick(r, "roi_total", "ROI") || 0),
              roiDirect:     parseFloat(pick(r, "Direct - ROI") || 0),
            };
          });
        const existing = JSON.parse(localStorage.getItem("3june_myntra_ads") || "[]");
        const keyOf = (a) => a.campaignId + "|" + (a.productId || a.campaignName || "");
        // Build a map keyed by campaign; NEW rows overwrite old (fixes stale spend=0 + duplicates)
        const byKey = {};
        existing.forEach(a => { byKey[keyOf(a)] = a; });
        let newCount = 0, updatedCount = 0;
        mapped.forEach(a => {
          const k = keyOf(a);
          if (byKey[k]) updatedCount++; else newCount++;
          byKey[k] = a;  // newest data always wins
        });
        const merged = Object.values(byKey);
        const uploads2 = JSON.parse(localStorage.getItem("3june_myntra_uploads") || "[]");
        uploads2.unshift({ type:"ads", file:file.name, period:"", count:mapped.length, newCount:newCount, date:new Date().toISOString() });
        localStorage.setItem("3june_myntra_ads",     JSON.stringify(merged));
        localStorage.setItem("3june_myntra_uploads", JSON.stringify(uploads2.slice(0,50)));
        setAds(merged);
        setUploads(uploads2.slice(0,50));
        if (newCount === 0 && updatedCount > 0) {
          toast(`Refreshed ${updatedCount} campaigns with latest data — ${merged.length} total`, "success");
        } else {
          toast(`✅ ${newCount} new + ${updatedCount} updated — ${merged.length} campaigns total`, "success");
        }
        setTab("ads");
      } else if (Object.keys(rows[0]).some(k=>["sale_order_code","seller_paid_amount","commission_total_amount"].includes(k))) {
        // Payment summary (part-01059 style)
        const mapped = rows.map(r=>({
          orderId:       r["sale_order_code"]||"",
          orderLineId:   r["order_number"]||"",
          invoiceNo:     r["invoice_number"]||"",
          sku:           r["product_sku_code"]||"",
          status:        r["order_item_status"]||"",
          orderDate:     (r["order_date"]||"").slice(0,10),
          deliveredOn:   (r["actual_delivery_date"]||"").slice(0,10),
          returnDate:    (r["return_date"]||"").slice(0,10),
          mrp:           parseFloat(r["mrp"]||0),
          customerPaid:  parseFloat(r["Total_Customer_Paid"]||r["seller_paid_amount"]||0),
          sellerPaid:    parseFloat(r["seller_paid_amount"]||0),
          discount:      parseFloat(r["discount_amount"]||0),
          commission:    parseFloat(r["commission_total_amount"]||0),
          commissionPct: parseFloat(r["commission_pct"]||0),
          tcs:           parseFloat(r["tcs_igst_amt"]||0)+parseFloat(r["tcs_cgst_amt"]||0)+parseFloat(r["tcs_sgst_amt"]||0),
          igst:          parseFloat(r["igst_amount"]||0),
          taxableAmt:    parseFloat(r["taxable_amount"]||0),
          state:         r["customer_state"]||"",
          pincode:       r["customer_pincode"]||"",
          tracking:      r["tracking_no"]||"",
          returnStatus:  r["return_status"]||"",
          returnType:    r["return_type"]||"",
          shippingCase:  r["shipping_case"]||"",
          utrFw:         r["bank_utr_no_prepaid_payment_fw"]||"",
          settleDateFw:  r["settlement_date_prepaid_payment_fw"]||"",
          taxRate:       parseFloat(r["tax_rate"]||18),
          hsn:           r["hsn"]||"42033000",
        }));
        const existing = JSON.parse(localStorage.getItem("3june_myntra_pay")||"[]");
        const existIds = new Set(existing.map(r=>r.orderId+r.sku));
        const newOnly  = mapped.filter(r=>!existIds.has(r.orderId+r.sku));
        const merged   = [...existing,...newOnly];
        localStorage.setItem("3june_myntra_pay", JSON.stringify(merged));
        setPayRows(merged);
        const u2 = JSON.parse(localStorage.getItem("3june_myntra_uploads")||"[]");
        u2.unshift({ type:"payment", file:file.name, period:"", count:mapped.length, newCount:newOnly.length, date:new Date().toISOString() });
        localStorage.setItem("3june_myntra_uploads", JSON.stringify(u2.slice(0,50)));
        setUploads(u2.slice(0,50));
        toast(`✅ ${newOnly.length} payment rows imported`, "success");
        setTab("finance");

      } else if (Object.keys(rows[0]).some(k=>["order release id","prepaid seller product amount","prepaid final settled amount"].includes(k))) {
        // Settlement report (20260608 style)
        const mapped = rows.map(r=>({
          orderId:          r["order release id"]||"",
          orderLineId:      r["order line id"]||"",
          sku:              r["sku code"]||"",
          styleName:        r["style name"]||"",
          sellerOrderId:    r["seller order id"]||"",
          mrp:              parseFloat(r["mrp"]||0),
          customerPaid:     parseFloat(r["customer paid amount"]||0),
          orderDate:        (r["order created on"]||"").slice(0,10),
          deliveredOn:      (r["delivered on"]||"").slice(0,10),
          returnOn:         (r["return on date"]||"").slice(0,10),
          returnStatus:     r["return status"]||"",
          fwdStatus:        r["forward payment status"]||"",
          revStatus:        r["reverse payment status"]||"",
          settleDue:        (r["settlement due date"]||"").slice(0,10),
          // Prepaid forward
          sellerProductAmt: parseFloat(r["prepaid seller product amount"]||0),
          commission:       parseFloat(r["prepaid total commission plus tcs tds"]||0),
          commissionPct:    parseFloat(r["prepaid commission"]||0),
          logistic:         parseFloat(r["prepaid total logistic charge"]||0),
          tcs:              parseFloat(r["prepaid tcs amount"]||0),
          tds:              parseFloat(r["prepaid tds amount"]||0),
          fixedCost:        parseFloat(r["prepaid fixed cost"]||0),
          finalSettled:     parseFloat(r["prepaid final settled amount"]||0),
          utr:              r["prepaid forward utr number"]||"",
          settleDate:       (r["prepaid forward settlement date"]||"").slice(0,10),
          payEligibleFw:    parseFloat(r["prepaid payment eligible forward"]||0),
          payEligibleRv:    parseFloat(r["prepaid payment eligible reverse"]||0),
          tracking:         r["forward tracking no"]||"",
          state:            r["customer statecode"]||"",
          zone:             r["shipment zone classification"]||"",
          invoiceNo:        r["invoice number"]||"",
        }));
        const existing = JSON.parse(localStorage.getItem("3june_myntra_settle")||"[]");
        const existIds = new Set(existing.map(r=>r.orderId));
        const newOnly  = mapped.filter(r=>!existIds.has(r.orderId));
        const merged   = [...existing,...newOnly];
        localStorage.setItem("3june_myntra_settle", JSON.stringify(merged));
        setSettleRows(merged);
        const u2 = JSON.parse(localStorage.getItem("3june_myntra_uploads")||"[]");
        u2.unshift({ type:"settlement", file:file.name, period:"", count:mapped.length, newCount:newOnly.length, date:new Date().toISOString() });
        localStorage.setItem("3jane_myntra_uploads", JSON.stringify(u2.slice(0,50)));
        setUploads(u2.slice(0,50));
        toast(`✅ ${newOnly.length} settlement rows imported`, "success");
        setTab("finance");

      } else {
        toast("Could not detect file type — check file format", "error");
      }
    } catch(e) { toast("Parse error: " + e.message, "error"); }
    setUploading(false);
  }, []);

  const clearAll = () => {
    if (!window.confirm("This will delete ALL Myntra orders, returns and upload history. Are you sure?")) return;
    setOrders([]); setReturns([]); setUploads([]); setAds([]); setAdditionalCosts([]); setCogDefaults({});
    localStorage.removeItem("3june_myntra_orders");
    localStorage.removeItem("3june_myntra_returns");
    localStorage.removeItem("3june_myntra_uploads");
    localStorage.removeItem("3june_myntra_ads");
    localStorage.removeItem("3june_myntra_cog");
    localStorage.removeItem("3june_myntra_extra");
    toast("All Myntra data cleared ✓", "success");
  };

  // COG helpers
  const saveCog = () => {}; // handled inline in form Save button

  // Download COG Excel template
  const downloadCogTemplate = () => {
    const header = "SKU,Product Name,Cost per Unit (₹)\n";
    const examples = [
      ...Object.entries(cogDefaults).map(([sku,v])=>`${sku},${v.name||""},${v.cost}`),
      // If no data yet, add example rows
      ...( Object.keys(cogDefaults).length === 0 ? [
        "AT_03_Black_36,Autolock Belt Black 36,280",
        "Rev_01_Brown_34,Reversible Belt Brown 34,300",
        "Br_01_Tan_34,Classic Belt Tan 34,250",
      ] : []),
    ].join("\n");
    const csv = header + examples;
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = "cog_template.csv";
    a.click();
    toast("Template downloaded ✓", "success");
  };

  // Parse uploaded COG Excel/CSV
  const handleCogUpload = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.trim().split("\n").filter(l=>l.trim());
      if (lines.length < 2) { toast("File is empty or invalid","error"); return; }

      // Detect header row
      const firstLine = lines[0].toLowerCase();
      const startIdx  = firstLine.includes("sku") || firstLine.includes("cost") ? 1 : 0;

      const updated = { ...cogDefaults };
      let imported = 0;
      let skipped  = 0;

      lines.slice(startIdx).forEach(line => {
        const parts = line.split(",").map(p=>p.replace(/^"|"$/g,"").trim());
        const sku   = parts[0];
        const name  = parts[1] || "";
        const cost  = parseFloat(parts[2]) || parseFloat(parts[1]) || 0;
        if (!sku || sku.toLowerCase()==="sku") return;
        if (cost <= 0) { skipped++; return; }
        updated[sku] = { name, cost };
        imported++;
      });

      if (imported === 0) { toast("No valid rows found. Check format: SKU, Name, Cost","error"); return; }
      setCogDefaults(updated);
      localStorage.setItem("3june_myntra_cog", JSON.stringify(updated));
      setShowCogUpload(false);
      toast(`✅ ${imported} SKUs imported${skipped?", "+skipped+" skipped (zero cost)":""}`, "success");
    } catch(e) {
      toast("Error reading file: " + e.message, "error");
    }
  };

  const saveExtra = () => {
    if (!extraForm.label || !extraForm.amount) { toast("Label and amount required","error"); return; }
    const updated = [...additionalCosts, { ...extraForm, id:Date.now() }];
    setAdditionalCosts(updated);
    localStorage.setItem("3june_myntra_extra", JSON.stringify(updated));
    setExtraForm({ label:"", amount:"", amountType:"per_order", period:"", category:"Packaging" });
    setShowExtraForm(false);
    toast("Additional cost added ✓","success");
  };

  const clearMonth = (month, type) => {
    if (!window.confirm(`Delete all ${type} data for ${month}?`)) return;
    if (type === "orders") {
      const updated = orders.filter(o => o.createdAt?.slice(0,7) !== month);
      setOrders(updated);
      localStorage.setItem("3june_myntra_orders", JSON.stringify(updated));
    } else {
      const updated = returns.filter(r => r.returnCreated?.slice(0,7) !== month);
      setReturns(updated);
      localStorage.setItem("3june_myntra_returns", JSON.stringify(updated));
    }
    const updatedUploads = uploads.filter(u => !(u.period === month && u.type === type));
    setUploads(updatedUploads);
    localStorage.setItem("3june_myntra_uploads", JSON.stringify(updatedUploads));
    toast(`${month} ${type} data deleted ✓`, "success");
  };

  // ── Computed financials ──────────────────────────────────────
  // Apply period filter
  const periodOrders  = periodFilter === "all" ? orders  : orders.filter(o => o.createdAt?.slice(0,7)  === periodFilter);
  const periodReturns = periodFilter === "all" ? returns : returns.filter(r => r.returnCreated?.slice(0,7) === periodFilter);

  const delivered    = periodOrders.filter(o => o.status === "C");
  const cancelled    = periodOrders.filter(o => ["X","KC","MC"].includes(o.status));
  const fulfilled    = periodOrders.filter(o => o.status === "F");
  const withReturns  = periodOrders.filter(o => o.returnCreated);

  const totalGMV     = delivered.reduce((a,o) => a + o.finalAmount, 0);
  const totalSeller  = delivered.reduce((a,o) => a + o.sellerPrice, 0);
  const totalGT      = delivered.reduce((a,o) => a + o.gtCharges, 0);
  const totalMRP     = delivered.reduce((a,o) => a + o.totalMRP, 0);
  const totalDiscount= delivered.reduce((a,o) => a + o.discount + o.couponDiscount, 0);
  const netPayout    = totalSeller - totalGT;
  const returnAmt    = periodReturns.filter(r=>r.isRefunded).length * (totalSeller / Math.max(delivered.length, 1));

  // GST (18% on seller price for leather belts — HSN 42033000, effective 15 Nov 2017)
  const gstRate      = GST_RATE; // 18% — single source of truth
  const outputGST    = totalSeller * gstRate / (100 + gstRate);
  const taxableValue = totalSeller - outputGST;

  // ── GST Report computed data ─────────────────────────────────
  const [gstPeriod, setGstPeriod] = useState("monthly");
  const [gstMonth,  setGstMonth]  = useState("");
  const [gstQuarter,setGstQuarter]= useState("Q1");
  const [gstYear,   setGstYear]   = useState("2026");

  // Get unique months from orders
  const orderMonths = [...new Set(
    orders.filter(o=>o.status==="C").map(o=>o.createdAt?.slice(0,7)).filter(Boolean)
  )].sort().reverse();

  // Filter delivered orders by selected period
  const gstOrders = orders.filter(o => {
    if (o.status !== "C") return false;
    const mo = o.createdAt?.slice(0,7) || "";
    if (gstPeriod === "monthly") return gstMonth ? mo === gstMonth : true;
    if (gstPeriod === "quarterly") {
      const [y,m] = mo.split("-").map(Number);
      const qMap = { Q1:[4,5,6], Q2:[7,8,9], Q3:[10,11,12], Q4:[1,2,3] };
      return String(y) === gstYear && (qMap[gstQuarter]||[]).includes(m);
    }
    return String(o.createdAt?.slice(0,4)) === gstYear;
  });

  // Per-order GST calculation
  const gstRows = gstOrders.map((o,i) => {
    const sp         = o.sellerPrice || 0;
    const outputGST  = Math.round(sp * GST_RATE / (100 + GST_RATE) * 100) / 100;
    const taxable    = Math.round((sp - outputGST) * 100) / 100;
    const isIntra    = (o.state || "").toUpperCase() === SELLER_STATE;
    const cgst       = isIntra ? Math.round(outputGST / 2 * 100) / 100 : 0;
    const sgst       = isIntra ? Math.round(outputGST / 2 * 100) / 100 : 0;
    const igst       = isIntra ? 0 : outputGST;
    const stCode     = (o.state || "").toUpperCase();
    const buyerState = STATE_NAMES[stCode] || stCode;
    const stGSTCode  = STATE_CODES[stCode] || "99";
    return {
      idx:         i + 1,
      invoiceNo:   `MYNT/${gstYear}/${String(i+1).padStart(4,"0")}`,
      date:        o.createdAt?.slice(0,10) || "",
      orderId:     o.orderId,
      sku:         o.sku,
      product:     o.productName,
      stateCode:   stCode,
      stateName:   buyerState,
      stGSTCode:   stGSTCode,
      isIntra,
      invoiceValue:Math.round(sp * 100) / 100,
      taxable,
      gstRate:     GST_RATE,
      cgst, sgst, igst,
      outputGST,
      gtCharges:   o.gtCharges || 0,
    };
  });

  // Totals
  const gstTotals = {
    invoiceValue: gstRows.reduce((a,r)=>a+r.invoiceValue, 0),
    taxable:      gstRows.reduce((a,r)=>a+r.taxable, 0),
    cgst:         gstRows.reduce((a,r)=>a+r.cgst, 0),
    sgst:         gstRows.reduce((a,r)=>a+r.sgst, 0),
    igst:         gstRows.reduce((a,r)=>a+r.igst, 0),
    outputGST:    gstRows.reduce((a,r)=>a+r.outputGST, 0),
    gtCharges:    gstRows.reduce((a,r)=>a+r.gtCharges, 0),
  };

  // State-wise GST summary
  const stateGST = {};
  gstRows.forEach(r => {
    if (!stateGST[r.stateCode]) stateGST[r.stateCode] = { state:r.stateName, code:r.stateCode, orders:0, taxable:0, cgst:0, sgst:0, igst:0, total:0 };
    stateGST[r.stateCode].orders++;
    stateGST[r.stateCode].taxable  += r.taxable;
    stateGST[r.stateCode].cgst     += r.cgst;
    stateGST[r.stateCode].sgst     += r.sgst;
    stateGST[r.stateCode].igst     += r.igst;
    stateGST[r.stateCode].total    += r.outputGST;
  });

  // Monthly summary for yearly/quarterly reports
  const monthlyGST = {};
  gstRows.forEach(r => {
    const mo = r.date.slice(0,7);
    if (!monthlyGST[mo]) monthlyGST[mo] = { month:mo, orders:0, taxable:0, cgst:0, sgst:0, igst:0, total:0 };
    monthlyGST[mo].orders++;
    monthlyGST[mo].taxable += r.taxable;
    monthlyGST[mo].cgst    += r.cgst;
    monthlyGST[mo].sgst    += r.sgst;
    monthlyGST[mo].igst    += r.igst;
    monthlyGST[mo].total   += r.outputGST;
  });

  // ── GST Export functions ──────────────────────────────────────
  const exportGSTR1Detail = () => {
    const headers = ["Sr","Invoice No","Invoice Date","Order ID","SKU","Product","Buyer State","State GST Code","Invoice Value","Taxable Value","GST Rate","CGST","SGST","IGST","Total GST","GT Charges"];
    const rows2   = gstRows.map(r=>[r.idx,r.invoiceNo,r.date,r.orderId,r.sku,r.product,r.stateName,r.stGSTCode,r.invoiceValue.toFixed(2),r.taxable.toFixed(2),r.gstRate+"%",r.cgst.toFixed(2),r.sgst.toFixed(2),r.igst.toFixed(2),r.outputGST.toFixed(2),r.gtCharges]);
    const csv     = [headers,...rows2].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const a=document.createElement("a"); a.href="data:text/csv,"+encodeURIComponent(csv); a.download=`Myntra_GSTR1_Detail_${gstPeriod}_${gstMonth||gstQuarter}_${gstYear}.csv`; a.click();
    toast("GSTR-1 detail exported ✓","success");
  };

  const exportGSTR1Summary = () => {
    const headers = ["State","State Code","Orders","Taxable Value","CGST","SGST","IGST","Total GST"];
    const rows2   = Object.values(stateGST).sort((a,b)=>b.total-a.total).map(r=>[r.state,r.code,r.orders,r.taxable.toFixed(2),r.cgst.toFixed(2),r.sgst.toFixed(2),r.igst.toFixed(2),r.total.toFixed(2)]);
    const csv     = [headers,...rows2].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const a=document.createElement("a"); a.href="data:text/csv,"+encodeURIComponent(csv); a.download=`Myntra_GSTR1_Summary_${gstPeriod}_${gstMonth||gstQuarter}_${gstYear}.csv`; a.click();
    toast("GSTR-1 summary exported ✓","success");
  };

  const exportTally = () => {
    const lines = gstRows.map(r=>`<TALLYMESSAGE xmlns:UDF="TallyUDF">
    <VOUCHER REMOTEID="${r.invoiceNo}" VCHTYPE="Sales" ACTION="Create">
      <DATE>${r.date.replace(/-/g,"")}</DATE>
      <NARRATION>Myntra Sale - ${r.product} - ${r.orderId}</NARRATION>
      <VOUCHERNUMBER>${r.invoiceNo}</VOUCHERNUMBER>
      <PARTYLEDGERNAME>Myntra Marketplace</PARTYLEDGERNAME>
      <AMOUNT>${r.invoiceValue.toFixed(2)}</AMOUNT>
      <TAXABLEVALUE>${r.taxable.toFixed(2)}</TAXABLEVALUE>
      ${r.isIntra
        ? `<CGSTAMOUNT>${r.cgst.toFixed(2)}</CGSTAMOUNT><SGSTAMOUNT>${r.sgst.toFixed(2)}</SGSTAMOUNT>`
        : `<IGSTAMOUNT>${r.igst.toFixed(2)}</IGSTAMOUNT>`}
      <HSNCODE>${HSN_CODE}</HSNCODE>
      <GSTRATE>${r.gstRate}</GSTRATE>
      <BUYERSTATE>${r.stateName}</BUYERSTATE>
    </VOUCHER>
  </TALLYMESSAGE>`).join("\n");
    const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<ENVELOPE>\n  <HEADER><TALLYREQUEST>Import Data</TALLYREQUEST></HEADER>\n  <BODY><IMPORTDATA><REQUESTDESC><REPORTNAME>Vouchers</REPORTNAME></REQUESTDESC>\n  <REQUESTDATA>\n${lines}\n  </REQUESTDATA></IMPORTDATA></BODY>\n</ENVELOPE>`;
    const a=document.createElement("a"); a.href="data:text/xml;charset=utf-8,"+encodeURIComponent(xml); a.download=`Myntra_Tally_${gstPeriod}_${gstMonth||gstQuarter}_${gstYear}.xml`; a.click();
    toast("Tally XML exported ✓","success");
  };

  // Print GST report
  const printGSTReport = () => {
    const periodLabel = gstPeriod==="monthly" ? (gstMonth||"All") : gstPeriod==="quarterly" ? `${gstQuarter} ${gstYear}` : `FY ${gstYear}-${String(parseInt(gstYear)+1).slice(2)}`;
    const html = `<!DOCTYPE html><html><head><title>Myntra GST Report</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;font-size:11px;color:#111}
      .header{background:#1A3A5C;color:#fff;padding:12px 16px;margin-bottom:12px}
      .header h1{font-size:16px;margin-bottom:4px}.header p{font-size:10px;opacity:.8}
      .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;font-size:10px}
      .info-box{border:1px solid #ddd;padding:8px;border-radius:4px}
      .info-box strong{display:block;font-size:9px;color:#666;text-transform:uppercase;margin-bottom:2px}
      .kpi-row{display:flex;gap:8px;margin-bottom:12px}
      .kpi{flex:1;border:1px solid #ddd;padding:8px;border-radius:4px;text-align:center}
      .kpi .val{font-size:14px;font-weight:700;color:#1A3A5C}.kpi .lbl{font-size:9px;color:#666;margin-top:2px}
      table{width:100%;border-collapse:collapse;margin-bottom:12px}
      th{background:#1A3A5C;color:#fff;padding:5px 7px;font-size:9px;text-align:left}
      td{padding:4px 7px;border-bottom:1px solid #eee;font-size:9px}
      .tfoot td{background:#f5f5f5;font-weight:700}
      .section-title{font-weight:700;font-size:12px;margin:12px 0 6px;border-bottom:1px solid #ddd;padding-bottom:4px}
      .note{background:#fffbeb;border:1px solid #fcd34d;padding:8px;font-size:9px;border-radius:4px;margin-top:8px}
      @media print{@page{size:A4;margin:10mm}}
    </style></head><body>
    <div class="header">
      <h1>Myntra GST Report — ${periodLabel}</h1>
      <p>Generated on ${new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"})} | 3June by Vasudeva Enterprises</p>
    </div>
    <div class="info-grid">
      <div class="info-box"><strong>Seller Name</strong>${SELLER_NAME}</div>
      <div class="info-box"><strong>GSTIN</strong>${SELLER_GSTIN}</div>
      <div class="info-box"><strong>HSN Code</strong>${HSN_CODE} — Leather Belts</div>
      <div class="info-box"><strong>GST Rate</strong>${GST_RATE}% (CGST ${GST_RATE/2}% + SGST ${GST_RATE/2}%)</div>
      <div class="info-box" style="grid-column:1/-1"><strong>Registered Address</strong>${SELLER_ADDR}</div>
    </div>
    <div class="kpi-row">
      <div class="kpi"><div class="val">${gstRows.length}</div><div class="lbl">Delivered Orders</div></div>
      <div class="kpi"><div class="val">₹${gstTotals.invoiceValue.toFixed(2)}</div><div class="lbl">Seller Price (incl. GST)</div></div>
      <div class="kpi"><div class="val">₹${gstTotals.taxable.toFixed(2)}</div><div class="lbl">Taxable Value</div></div>
      <div class="kpi"><div class="val">₹${gstTotals.igst.toFixed(2)}</div><div class="lbl">IGST</div></div>
      <div class="kpi"><div class="val">₹${gstTotals.cgst.toFixed(2)}</div><div class="lbl">CGST</div></div>
      <div class="kpi"><div class="val">₹${gstTotals.sgst.toFixed(2)}</div><div class="lbl">SGST</div></div>
      <div class="kpi"><div class="val">₹${gstTotals.outputGST.toFixed(2)}</div><div class="lbl">Total Output GST</div></div>
    </div>
    <div class="section-title">Order-wise GSTR-1 Details</div>
    <table>
      <thead><tr><th>#</th><th>Invoice No</th><th>Date</th><th>Order ID</th><th>SKU</th><th>Buyer State</th><th>Taxable Value</th><th>CGST</th><th>SGST</th><th>IGST</th><th>Total GST</th></tr></thead>
      <tbody>
        ${gstRows.map(r=>`<tr><td>${r.idx}</td><td>${r.invoiceNo}</td><td>${r.date}</td><td>${r.orderId}</td><td>${r.sku}</td><td>${r.stateName} (${r.stateCode})</td><td>₹${r.taxable.toFixed(2)}</td><td>${r.cgst>0?"₹"+r.cgst.toFixed(2):"—"}</td><td>${r.sgst>0?"₹"+r.sgst.toFixed(2):"—"}</td><td>${r.igst>0?"₹"+r.igst.toFixed(2):"—"}</td><td>₹${r.outputGST.toFixed(2)}</td></tr>`).join("")}
      </tbody>
      <tfoot><tr class="tfoot"><td colspan="6">TOTAL (${gstRows.length} orders)</td><td>₹${gstTotals.taxable.toFixed(2)}</td><td>₹${gstTotals.cgst.toFixed(2)}</td><td>₹${gstTotals.sgst.toFixed(2)}</td><td>₹${gstTotals.igst.toFixed(2)}</td><td>₹${gstTotals.outputGST.toFixed(2)}</td></tr></tfoot>
    </table>
    <div class="section-title">State-wise GST Summary</div>
    <table>
      <thead><tr><th>State</th><th>Orders</th><th>Taxable Value</th><th>CGST</th><th>SGST</th><th>IGST</th><th>Total GST</th></tr></thead>
      <tbody>${Object.values(stateGST).sort((a,b)=>b.total-a.total).map(r=>`<tr><td>${r.state} (${r.code})</td><td>${r.orders}</td><td>₹${r.taxable.toFixed(2)}</td><td>${r.cgst>0?"₹"+r.cgst.toFixed(2):"—"}</td><td>${r.sgst>0?"₹"+r.sgst.toFixed(2):"—"}</td><td>${r.igst>0?"₹"+r.igst.toFixed(2):"—"}</td><td>₹${r.total.toFixed(2)}</td></tr>`).join("")}</tbody>
    </table>
    <div class="note">
      ⚠ Note: This report is generated from Myntra MDirect Orders CSV. GST is calculated on Seller Price (net of Myntra's GT charges). 
      Verify all figures against your Myntra payment statement before filing GSTR-1. HSN 42033000 — Leather Belts & Accessories — ${GST_RATE}% GST.
      For inter-state sales (IGST), place of supply = buyer's state as per Myntra delivery address.
    </div>
    </body></html>`;
    const w = window.open("","_blank","width=900,height=700");
    w.document.write(html);
    w.document.close();
    setTimeout(()=>w.print(),600);
    toast("GST report sent to printer ✓","success");
  };

  // ── Filtered orders ──────────────────────────────────────────
  const filteredOrders = periodOrders.filter(o => {
    const q = search.toLowerCase();
    const matchSearch = !q || o.orderId.toLowerCase().includes(q) || o.sku.toLowerCase().includes(q) || o.productName.toLowerCase().includes(q);
    const matchStatus = filterStatus === "all" || o.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // ── Reconciliation ───────────────────────────────────────────
  const reconRows = delivered.map(o => {
    const ret       = returns.find(r => r.orderId === o.orderId);
    const hasReturn = !!ret || !!o.returnCreated;
    const netAmt    = hasReturn ? 0 : o.sellerPrice - o.gtCharges;
    return { ...o, hasReturn, ret, netAmt };
  });

  // ── SKU analytics ─────────────────────────────────────────────
  const skuMap = {};
  delivered.forEach(o => {
    if (!skuMap[o.sku]) skuMap[o.sku] = { sku:o.sku, name:o.productName, qty:0, revenue:0, sellerRevenue:0 };
    skuMap[o.sku].qty++;
    skuMap[o.sku].revenue       += o.finalAmount;
    skuMap[o.sku].sellerRevenue += o.sellerPrice;
  });
  const skuList = Object.values(skuMap).sort((a,b) => b.qty - a.qty);

  const stateMap = {};
  delivered.forEach(o => {
    if (!stateMap[o.state]) stateMap[o.state] = 0;
    stateMap[o.state]++;
  });
  const stateList = Object.entries(stateMap).sort((a,b)=>b[1]-a[1]);

  const exportCSV = (data, filename) => {
    if (!data.length) { toast("No data to export", "error"); return; }
    const headers = Object.keys(data[0]);
    const csv = [headers.join(","), ...data.map(r => headers.map(h => `"${r[h]??''}"`).join(","))].join("\n");
    const a = document.createElement("a"); a.href="data:text/csv,"+encodeURIComponent(csv); a.download=filename+".csv"; a.click();
    toast("Exported ✓", "success");
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16, display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ background:"#b5003c", color:"#fff", fontSize:11, padding:"2px 8px", borderRadius:6, fontWeight:700 }}>M</span>
            Myntra Reports
          </div>
          <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>Import Myntra orders & returns CSV · Finance · Reconciliation · Analytics</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {(orders.length > 0 || returns.length > 0) && (
            <button style={S.btnOut} onClick={clearAll}>🗑 Clear Data</button>
          )}
          <div style={{ fontSize:11, color:"#9ca3af", alignSelf:"center", display:"flex", gap:12 }}>
            {orders.length  > 0 && <span>📦 <strong>{orders.length}</strong> orders (all time)</span>}
            {returns.length > 0 && <span>↩️ <strong>{returns.length}</strong> returns (all time)</span>}
            {uploads.length > 0 && <span>📁 <strong>{uploads.length}</strong> uploads</span>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:14, overflowX:"auto" }}>
        {TABS.map(t => (
          <button key={t.id} style={{ padding:"9px 16px", border:"none", borderBottom:tab===t.id?"2px solid #b5003c":"2px solid transparent", background:"none", color:tab===t.id?"#b5003c":"#6b7280", fontWeight:tab===t.id?600:400, fontSize:12, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}
            onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ── Period filter (all tabs except upload) ── */}
      {tab !== "upload" && (() => {
        const months = [...new Set([
          ...orders.map(o=>o.createdAt?.slice(0,7)),
          ...returns.map(r=>r.returnCreated?.slice(0,7)),
        ].filter(Boolean))].sort().reverse();
        return months.length > 1 ? (
          <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:12, flexWrap:"wrap", background:"#f9fafb", padding:"8px 12px", borderRadius:8, border:"1px solid #e5e7eb" }}>
            <span style={{ fontSize:11, fontWeight:600, color:"#6b7280", flexShrink:0 }}>📅 Period:</span>
            <button onClick={()=>setPeriodFilter("all")} style={{ padding:"4px 12px", borderRadius:20, fontSize:11, cursor:"pointer", border:"none", fontFamily:"inherit", fontWeight:600, background:periodFilter==="all"?"#b5003c":"#fff", color:periodFilter==="all"?"#fff":"#374151" }}>
              All ({orders.length})
            </button>
            {months.map(m=>{
              const mOrders = orders.filter(o=>o.createdAt?.slice(0,7)===m).length;
              return (
                <button key={m} onClick={()=>setPeriodFilter(m)} style={{ padding:"4px 12px", borderRadius:20, fontSize:11, cursor:"pointer", border:"1px solid #e5e7eb", fontFamily:"inherit", fontWeight:600, background:periodFilter===m?"#b5003c":"#fff", color:periodFilter===m?"#fff":"#374151" }}>
                  {m} ({mOrders})
                </button>
              );
            })}
            {periodFilter!=="all" && (
              <button onClick={()=>setPeriodFilter("all")} style={{ padding:"4px 8px", borderRadius:6, fontSize:10, cursor:"pointer", background:"#fef2f2", color:"#dc2626", border:"none", fontFamily:"inherit", marginLeft:"auto" }}>
                Clear ×
              </button>
            )}
          </div>
        ) : null;
      })()}

      {/* ══ UPLOAD ══ */}
      {tab === "upload" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          {/* Orders upload */}
          <div style={S.card}>
            <div style={S.hdr}><span>🛒 Upload Orders Report</span></div>
            <div style={{ padding:20 }}>
              <div style={{ background:"#fff0f5", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#b5003c", marginBottom:14 }}>
                <strong>How to get this file:</strong><br/>
                Myntra Seller Portal → Reports → Order Reports → Download CSV
              </div>
              <div
                onClick={() => ordRef.current?.click()}
                onDragOver={e=>e.preventDefault()}
                onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0],"orders");}}
                style={{ border:"2px dashed #e5e7eb", borderRadius:10, padding:40, textAlign:"center", cursor:"pointer", background:"#fafafa" }}>
                <input ref={ordRef} type="file" accept=".csv" style={{ display:"none" }} onChange={e=>handleFile(e.target.files[0],"orders")}/>
                <div style={{ fontSize:36, marginBottom:8 }}>📊</div>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>Drop Orders CSV here</div>
                <div style={{ fontSize:11, color:"#9ca3af" }}>MDirect_Orders_Report_*.csv</div>
                {orders.length > 0 && <div style={{ marginTop:10, fontSize:11, color:"#15803d", fontWeight:600 }}>✅ {orders.length} orders loaded — re-upload to refresh</div>}
              </div>
              {uploading && <div style={{ textAlign:"center", color:"#b5003c", fontSize:12, marginTop:8 }}>⏳ Processing…</div>}
            </div>
          </div>

          {/* Returns upload */}
          <div style={S.card}>
            <div style={S.hdr}><span>↩️ Upload Returns Report</span></div>
            <div style={{ padding:20 }}>
              <div style={{ background:"#fff0f5", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#b5003c", marginBottom:14 }}>
                <strong>How to get this file:</strong><br/>
                Myntra Seller Portal → Reports → Return Reports → Download CSV
              </div>
              <div
                onClick={() => retRef.current?.click()}
                onDragOver={e=>e.preventDefault()}
                onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0],"returns");}}
                style={{ border:"2px dashed #e5e7eb", borderRadius:10, padding:40, textAlign:"center", cursor:"pointer", background:"#fafafa" }}>
                <input ref={retRef} type="file" accept=".csv" style={{ display:"none" }} onChange={e=>handleFile(e.target.files[0],"returns")}/>
                <div style={{ fontSize:36, marginBottom:8 }}>↩️</div>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>Drop Returns CSV here</div>
                <div style={{ fontSize:11, color:"#9ca3af" }}>MDirect_Returns_Report_*.csv</div>
                {returns.length > 0 && <div style={{ marginTop:10, fontSize:11, color:"#15803d", fontWeight:600 }}>✅ {returns.length} returns loaded — re-upload to refresh</div>}
              </div>
            </div>
          </div>

          {/* Ads upload */}
          <div style={{ ...S.card, gridColumn:"1/-1" }}>
            <div style={S.hdr}><span>📢 Upload Ads / PLA Report</span></div>
            <div style={{ padding:20 }}>
              <div style={{ background:"#fff0f5", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#b5003c", marginBottom:14 }}>
                <strong>How to get this file:</strong><br/>
                Myntra Seller Portal → Advertising → Reports → Download Campaign Report CSV
              </div>
              <div
                onClick={() => adFileRef.current?.click()}
                onDragOver={e=>e.preventDefault()}
                onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0],"ads");}}
                style={{ border:"2px dashed #e5e7eb", borderRadius:10, padding:24, textAlign:"center", cursor:"pointer", background:"#fafafa" }}>
                <input ref={adFileRef} type="file" accept=".csv" style={{ display:"none" }} onChange={e=>handleFile(e.target.files[0],"ads")}/>
                <div style={{ fontSize:28, marginBottom:6 }}>📢</div>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>Drop Ads Report CSV here</div>
                <div style={{ fontSize:11, color:"#9ca3af" }}>report_REPID*.csv</div>
                {ads.length > 0 && <div style={{ marginTop:8, fontSize:11, color:"#15803d", fontWeight:600 }}>✅ {ads.length} ad rows loaded — re-upload to add more</div>}
              </div>
            </div>
          </div>

          {/* Payment Summary upload */}
          <div style={{ ...S.card, gridColumn:"1/-1" }}>
            <div style={S.hdr}><span>💳 Upload Payment / Settlement Reports</span></div>
            <div style={{ padding:14, display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:12, marginBottom:6, color:"#2563eb" }}>📄 Payment Summary CSV</div>
                <div style={{ fontSize:11, color:"#9ca3af", marginBottom:10 }}>Myntra Portal → Payments → Payment Summary → Download</div>
                <div
                  onClick={()=>payFileRef.current?.click()}
                  onDragOver={e=>e.preventDefault()}
                  onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0],"payment");}}
                  style={{ border:"2px dashed #bfdbfe", borderRadius:8, padding:"20px", textAlign:"center", cursor:"pointer", background:"#eff6ff" }}>
                  <input ref={payFileRef} type="file" accept=".csv" style={{ display:"none" }} onChange={e=>handleFile(e.target.files[0],"payment")}/>
                  <div style={{ fontSize:24, marginBottom:4 }}>💳</div>
                  <div style={{ fontSize:12, fontWeight:600, color:"#2563eb" }}>Drop payment CSV</div>
                  <div style={{ fontSize:10, color:"#9ca3af" }}>part-01059-*.csv</div>
                  {payRows.length>0&&<div style={{ marginTop:6, fontSize:11, color:"#15803d", fontWeight:600 }}>✅ {payRows.length} rows loaded</div>}
                </div>
              </div>
              <div>
                <div style={{ fontWeight:600, fontSize:12, marginBottom:6, color:"#7e22ce" }}>📊 Settlement Report CSV</div>
                <div style={{ fontSize:11, color:"#9ca3af", marginBottom:10 }}>Myntra Portal → Payments → Settlement Report → Download</div>
                <div
                  onClick={()=>settleFileRef.current?.click()}
                  onDragOver={e=>e.preventDefault()}
                  onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0],"settlement");}}
                  style={{ border:"2px dashed #e9d5ff", borderRadius:8, padding:"20px", textAlign:"center", cursor:"pointer", background:"#fdf4ff" }}>
                  <input ref={settleFileRef} type="file" accept=".csv" style={{ display:"none" }} onChange={e=>handleFile(e.target.files[0],"settlement")}/>
                  <div style={{ fontSize:24, marginBottom:4 }}>📊</div>
                  <div style={{ fontSize:12, fontWeight:600, color:"#7e22ce" }}>Drop settlement CSV</div>
                  <div style={{ fontSize:10, color:"#9ca3af" }}>20260608_*.csv</div>
                  {settleRows.length>0&&<div style={{ marginTop:6, fontSize:11, color:"#15803d", fontWeight:600 }}>✅ {settleRows.length} rows loaded</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Upload History */}
          <div style={{ ...S.card, gridColumn:"1/-1" }}>
            <div style={S.hdr}>
              <span>📁 Upload History — All Stored Data</span>
              <span style={{ fontSize:11, color:"#9ca3af", fontWeight:400 }}>
                {orders.length} orders · {returns.length} returns stored
              </span>
            </div>
            {uploads.length === 0 ? (
              <div style={{ padding:24, textAlign:"center", color:"#9ca3af", fontSize:12 }}>
                No uploads yet — upload your first Myntra report above
              </div>
            ) : (
              <div>
                {/* Summary by month */}
                {(() => {
                  const months = [...new Set([
                    ...orders.map(o=>o.createdAt?.slice(0,7)),
                    ...returns.map(r=>r.returnCreated?.slice(0,7)),
                  ].filter(Boolean))].sort().reverse();
                  return (
                    <div style={{ padding:14, borderBottom:"1px solid #e5e7eb" }}>
                      <div style={{ fontWeight:600, fontSize:12, marginBottom:10 }}>📅 Data stored by month:</div>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
                        {months.map(m => {
                          const mOrders  = orders.filter(o=>o.createdAt?.slice(0,7)===m);
                          const mReturns = returns.filter(r=>r.returnCreated?.slice(0,7)===m);
                          const mGMV     = mOrders.filter(o=>o.status==="C").reduce((a,o)=>a+o.sellerPrice,0);
                          return (
                            <div key={m} style={{ background:"#f9fafb", borderRadius:8, padding:"10px 12px", border:"1px solid #e5e7eb" }}>
                              <div style={{ fontWeight:700, fontSize:13, color:"#b5003c", marginBottom:4 }}>{m}</div>
                              <div style={{ fontSize:11, color:"#374151" }}>{mOrders.length} orders · {mReturns.length} returns</div>
                              <div style={{ fontSize:11, color:"#15803d", fontWeight:600 }}>₹{Math.round(mGMV).toLocaleString("en-IN")} seller earn</div>
                              <div style={{ display:"flex", gap:6, marginTop:8 }}>
                                <button style={{ flex:1, padding:"3px 0", borderRadius:6, fontSize:10, cursor:"pointer", background:"#b5003c", color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }}
                                  onClick={()=>setPeriodFilter(m)}>View</button>
                                <button style={{ padding:"3px 8px", borderRadius:6, fontSize:10, cursor:"pointer", background:"#fef2f2", color:"#dc2626", border:"none", fontFamily:"inherit" }}
                                  onClick={()=>clearMonth(m,"orders")}>Del</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Upload log */}
                <div style={{ padding:14 }}>
                  <div style={{ fontWeight:600, fontSize:12, marginBottom:10 }}>📋 Recent Uploads:</div>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>{["File","Type","Period","New Records","Total Records","Uploaded At"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {uploads.map((u,i)=>(
                        <tr key={i} style={{ background:i%2?"#fafafa":"#fff" }}>
                          <td style={{ ...S.td, fontSize:10, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", color:"#6b7280" }}>{u.file}</td>
                          <td style={S.td}>
                            <span style={{ fontSize:10, padding:"1px 7px", borderRadius:8, background:u.type==="orders"?"#fff0f5":"#fef2f2", color:"#b5003c", fontWeight:600 }}>
                              {u.type==="orders"?"🛒 Orders":"↩️ Returns"}
                            </span>
                          </td>
                          <td style={{ ...S.td, fontWeight:600, color:"#b5003c" }}>{u.period}</td>
                          <td style={{ ...S.td, color:"#15803d", fontWeight:600 }}>+{u.newCount}</td>
                          <td style={S.td}>{u.count}</td>
                          <td style={{ ...S.td, fontSize:10, color:"#9ca3af" }}>{new Date(u.date).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"2-digit",hour:"2-digit",minute:"2-digit"})}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ ORDERS ══ */}
      {tab === "orders" && (
        <div>
          {/* KPIs */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:14 }}>
            {[
              { label:"Total Orders",   value:orders.length,    color:"#b5003c" },
              { label:"Delivered",      value:delivered.length, color:"#15803d" },
              { label:"Cancelled/RTO",  value:cancelled.length+fulfilled.length, color:"#dc2626" },
              { label:"With Returns",   value:withReturns.length, color:"#d97706" },
              { label:"GMV",            value:INR(totalGMV),    color:"#7e22ce" },
            ].map(k=>(
              <div key={k.label} style={{ ...S.kpi(k.color) }}>
                <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                <div style={{ fontSize:20, fontWeight:700, color:k.color, marginTop:2 }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div style={{ display:"flex", gap:8, marginBottom:10, flexWrap:"wrap" }}>
            <input style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none", width:200 }}
              placeholder="🔍 Search order ID, SKU…" value={search} onChange={e=>setSearch(e.target.value)}/>
            <select style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none" }}
              value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="C">Delivered</option>
              <option value="F">Fulfilled/Cancelled</option>
              <option value="X">Cancelled</option>
            </select>
            <span style={{ fontSize:11, color:"#9ca3af", alignSelf:"center" }}>{filteredOrders.length} orders</span>
            <button style={{ ...S.btnOut, marginLeft:"auto" }} onClick={()=>exportCSV(orders,"myntra_orders")}>⬇ Export</button>
          </div>

          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>{["Order ID","Date","SKU","Product","Size","Status","MRP","Final Amt","Seller Price","GT Charges","City","State","Tracking"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {orders.length===0 ? (
                    <tr><td colSpan={13} style={{ ...S.td, textAlign:"center", padding:40, color:"#9ca3af" }}>Upload Myntra orders CSV to see data</td></tr>
                  ) : filteredOrders.map((o,i)=>(
                    <tr key={o.orderId+i} style={{ background:i%2?"#fafafa":"#fff" }}>
                      <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#b5003c" }}>{o.orderId}</td>
                      <td style={{ ...S.td, fontSize:10 }}>{o.createdAt?.slice(0,10)}</td>
                      <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{o.sku}</td>
                      <td style={{ ...S.td, maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{o.productName}</td>
                      <td style={{ ...S.td, textAlign:"center" }}>{o.size}</td>
                      <td style={S.td}>
                        <span style={{ fontSize:9, padding:"2px 7px", borderRadius:10, background:(STATUS_COLOR[o.status]||"#9ca3af")+"18", color:STATUS_COLOR[o.status]||"#9ca3af", fontWeight:700 }}>
                          {STATUS_MAP[o.status]||o.status}
                        </span>
                      </td>
                      <td style={{ ...S.td, textAlign:"right" }}>{INR(o.totalMRP)}</td>
                      <td style={{ ...S.td, textAlign:"right", fontWeight:600 }}>{INR(o.finalAmount)}</td>
                      <td style={{ ...S.td, textAlign:"right", color:"#15803d", fontWeight:600 }}>{INR(o.sellerPrice)}</td>
                      <td style={{ ...S.td, textAlign:"right", color:"#dc2626" }}>{INR(o.gtCharges)}</td>
                      <td style={S.td}>{o.city}</td>
                      <td style={S.td}>{o.state}</td>
                      <td style={{ ...S.td, fontFamily:"monospace", fontSize:10 }}>{o.tracking}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══ RETURNS ══ */}
      {tab === "returns" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
            {[
              { label:"Total Returns",  value:periodReturns.length,                                    color:"#dc2626" },
              { label:"Refunded",       value:returns.filter(r=>r.isRefunded).length,             color:"#d97706" },
              { label:"Top Reason",     value:(() => { const m={}; returns.forEach(r=>{if(r.returnReason)m[r.returnReason]=(m[r.returnReason]||0)+1}); return Object.entries(m).sort((a,b)=>b[1]-a[1])[0]?.[0]?.slice(0,20)||"—"; })(), color:"#6b7280" },
              { label:"Return Rate",    value:periodOrders.length>0?Math.round(periodReturns.length/periodOrders.length*100)+"%":"—", color:"#7e22ce" },
            ].map(k=>(
              <div key={k.label} style={{ ...S.kpi(k.color) }}>
                <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                <div style={{ fontSize:16, fontWeight:700, color:k.color, marginTop:2 }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={S.hdr}><span>↩️ Returns List</span><button style={S.btnOut} onClick={()=>exportCSV(returns,"myntra_returns")}>⬇ Export</button></div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>{["Return ID","Order ID","SKU","Reason","Mode","Status","Refunded","Return Date","Fwd Tracking","Ret Tracking"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {periodReturns.length===0 ? (
                  <tr><td colSpan={10} style={{ ...S.td, textAlign:"center", padding:40, color:"#9ca3af" }}>Upload Myntra returns CSV to see data</td></tr>
                ) : periodReturns.map((r,i)=>(
                  <tr key={r.returnId+i} style={{ background:i%2?"#fafafa":"#fff" }}>
                    <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#b5003c" }}>{r.returnId||"—"}</td>
                    <td style={{ ...S.td, fontFamily:"monospace", fontSize:10 }}>{r.orderId}</td>
                    <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{r.sku}</td>
                    <td style={S.td}>{r.returnReason||"—"}</td>
                    <td style={S.td}><span style={{ fontSize:9, background:"#f3f4f6", padding:"1px 6px", borderRadius:6 }}>{r.returnMode||"—"}</span></td>
                    <td style={S.td}><span style={{ fontSize:9, background:"#eff6ff", color:"#2563eb", padding:"1px 6px", borderRadius:6, fontWeight:600 }}>{r.returnStatus||"—"}</span></td>
                    <td style={S.td}><span style={{ fontSize:9, padding:"2px 7px", borderRadius:10, background:r.isRefunded?"#f0fdf4":"#fef2f2", color:r.isRefunded?"#15803d":"#dc2626", fontWeight:700 }}>{r.isRefunded?"Yes":"No"}</span></td>
                    <td style={{ ...S.td, fontSize:10 }}>{r.returnCreated?.slice(0,10)||"—"}</td>
                    <td style={{ ...S.td, fontFamily:"monospace", fontSize:9 }}>{r.fwdTracking||"—"}</td>
                    <td style={{ ...S.td, fontFamily:"monospace", fontSize:9 }}>{r.retTracking||"—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ FINANCE ══ */}
      {tab === "finance" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            {/* P&L */}
            <div style={S.card}>
              <div style={S.hdr}><span>💰 Myntra P&L Statement</span></div>
              <div style={{ padding:14 }}>
                {[
                  { label:"Gross GMV (Customer paid)",   value:totalGMV,      color:"#15803d", bold:false },
                  { label:"Total MRP value",             value:totalMRP,      color:"#6b7280", bold:false },
                  { label:"Platform discount given",     value:-totalDiscount, color:"#dc2626", bold:false },
                  { label:"",                            value:null },
                  { label:"Your Seller Price (Gross)",   value:totalSeller,   color:"#15803d", bold:true  },
                  { label:"GT Charges / Commission",     value:-totalGT,      color:"#dc2626", bold:false },
                  { label:"Net Payout (before GST)",     value:netPayout,     color:"#15803d", bold:true  },
                  { label:"",                            value:null },
                  { label:"Output GST @18% (HSN 42033000)", value:outputGST,  color:"#7e22ce", bold:false },
                  { label:"Taxable value (Net)",         value:taxableValue,  color:"#2563eb", bold:false },
                  { label:"",                            value:null },
                  { label:"Estimated Return Loss",       value:-returnAmt,    color:"#dc2626", bold:false },
                  { label:"Net Effective Earnings",      value:netPayout-outputGST-returnAmt, color:netPayout-outputGST-returnAmt>=0?"#15803d":"#dc2626", bold:true },
                ].map((r,i)=>{
                  if (r.value===null) return <div key={i} style={{ height:8 }}/>;
                  return (
                    <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:r.bold?"9px 0":"5px 0", borderBottom:r.bold?"2px solid #e5e7eb":"1px solid #f9fafb" }}>
                      <span style={{ fontSize:12, fontWeight:r.bold?700:400, color:r.bold?"#1A3A5C":"#6b7280" }}>{r.label}</span>
                      <span style={{ fontSize:12, fontWeight:r.bold?700:500, color:r.color }}>{INR(Math.abs(r.value))}{r.value<0?" (Dr)":""}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* GST Summary */}
            <div>
              <div style={S.card}>
                <div style={S.hdr}><span>🧾 GST Summary — Myntra</span></div>
                <div style={{ padding:14 }}>
                  {[
                    { label:"Seller Price (incl. GST)",   value:totalSeller   },
                    { label:"Taxable Value",              value:taxableValue  },
                    { label:"Output GST @18%",            value:outputGST     },
                    { label:"CGST @9%",                   value:outputGST/2   },
                    { label:"SGST @9% (same state)",      value:outputGST/2   },
                    { label:"IGST @18% (inter-state)",    value:outputGST     },
                  ].map(r=>(
                    <div key={r.label} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid #f9fafb", fontSize:12 }}>
                      <span style={{ color:"#6b7280" }}>{r.label}</span>
                      <span style={{ fontWeight:600 }}>{INR(r.value)}</span>
                    </div>
                  ))}
                  <div style={{ marginTop:10, background:"#eff6ff", borderRadius:6, padding:"8px 10px", fontSize:11, color:"#1e40af" }}>
                    💡 Myntra deducts TCS @1% and platform fees from payout. Always match against Myntra payment statement.
                  </div>
                </div>
              </div>

              <div style={S.card}>
                <div style={S.hdr}><span>📊 Commission Analysis</span></div>
                <div style={{ padding:14 }}>
                  {[
                    { label:"Avg GT Charges per order",   value:INR(totalGT/Math.max(delivered.length,1)) },
                    { label:"GT as % of seller price",    value:PCT(totalGT, totalSeller) },
                    { label:"Discount ratio",             value:PCT(totalDiscount, totalMRP) },
                    { label:"Avg seller price per order", value:INR(totalSeller/Math.max(delivered.length,1)) },
                    { label:"Avg GMV per order",          value:INR(totalGMV/Math.max(delivered.length,1)) },
                  ].map(r=>(
                    <div key={r.label} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid #f9fafb", fontSize:12 }}>
                      <span style={{ color:"#6b7280" }}>{r.label}</span>
                      <span style={{ fontWeight:600 }}>{r.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* ══ GST REPORT ══ */}
      {tab === "gst" && (
        <div>
          {orders.length === 0 ? (
            <div style={{ ...S.card, padding:48, textAlign:"center" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🧾</div>
              <div style={{ fontWeight:600, fontSize:14, marginBottom:6 }}>No orders uploaded yet</div>
              <div style={{ fontSize:12, color:"#9ca3af", marginBottom:16 }}>Upload your Myntra orders CSV first, then come back here to generate GST reports</div>
              <button style={{ ...S.btn("#b5003c") }} onClick={()=>setTab("upload")}>📁 Go to Upload →</button>
            </div>
          ) : (
            <div>
              {/* Period selector */}
              <div style={{ ...S.card, padding:16, marginBottom:14 }}>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>🗓️ Select Report Period</div>
                <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"flex-end" }}>
                  {/* Period type */}
                  <div>
                    <div style={{ fontSize:11, color:"#6b7280", fontWeight:600, marginBottom:4 }}>Report Type</div>
                    <div style={{ display:"flex", gap:6 }}>
                      {["monthly","quarterly","yearly"].map(p=>(
                        <button key={p} style={{ padding:"7px 16px", borderRadius:7, fontSize:11, cursor:"pointer", fontFamily:"inherit", fontWeight:600, border:"none", background:gstPeriod===p?"#b5003c":"#f3f4f6", color:gstPeriod===p?"#fff":"#374151" }}
                          onClick={()=>setGstPeriod(p)}>{p.charAt(0).toUpperCase()+p.slice(1)}</button>
                      ))}
                    </div>
                  </div>

                  {/* Monthly picker */}
                  {gstPeriod === "monthly" && (
                    <div>
                      <div style={{ fontSize:11, color:"#6b7280", fontWeight:600, marginBottom:4 }}>Month</div>
                      <select style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none" }}
                        value={gstMonth} onChange={e=>setGstMonth(e.target.value)}>
                        <option value="">All months</option>
                        {orderMonths.map(m=><option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                  )}

                  {/* Quarterly picker */}
                  {gstPeriod === "quarterly" && (
                    <>
                      <div>
                        <div style={{ fontSize:11, color:"#6b7280", fontWeight:600, marginBottom:4 }}>Quarter (GST FY Apr–Mar)</div>
                        <select style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none" }}
                          value={gstQuarter} onChange={e=>setGstQuarter(e.target.value)}>
                          <option value="Q1">Q1 — Apr–Jun</option>
                          <option value="Q2">Q2 — Jul–Sep</option>
                          <option value="Q3">Q3 — Oct–Dec</option>
                          <option value="Q4">Q4 — Jan–Mar</option>
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:"#6b7280", fontWeight:600, marginBottom:4 }}>Year</div>
                        <select style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none" }}
                          value={gstYear} onChange={e=>setGstYear(e.target.value)}>
                          {["2024","2025","2026","2027"].map(y=><option key={y}>{y}</option>)}
                        </select>
                      </div>
                    </>
                  )}

                  {/* Yearly picker */}
                  {gstPeriod === "yearly" && (
                    <div>
                      <div style={{ fontSize:11, color:"#6b7280", fontWeight:600, marginBottom:4 }}>Financial Year (Apr–Mar)</div>
                      <select style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none" }}
                        value={gstYear} onChange={e=>setGstYear(e.target.value)}>
                        {["2024","2025","2026","2027"].map(y=><option key={y}>{y}-{String(parseInt(y)+1).slice(2)}</option>)}
                      </select>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div style={{ marginLeft:"auto", display:"flex", gap:8, flexWrap:"wrap" }}>
                    <button style={{ ...S.btn("#1A3A5C") }} onClick={printGSTReport}>🖨️ Print Report</button>
                    <button style={{ ...S.btn("#15803d") }} onClick={exportGSTR1Detail}>⬇ GSTR-1 CSV</button>
                    <button style={{ ...S.btnOut }} onClick={exportGSTR1Summary}>⬇ State Summary</button>
                    <button style={{ ...S.btnOut }} onClick={exportTally}>📤 Tally XML</button>
                  </div>
                </div>

                {/* Period indicator */}
                <div style={{ marginTop:12, background:"#fff0f5", borderRadius:8, padding:"8px 12px", fontSize:12, color:"#b5003c" }}>
                  📊 Showing GST data for <strong>{gstRows.length}</strong> delivered orders
                  {gstPeriod==="monthly" && gstMonth ? ` — ${gstMonth}` : ""}
                  {gstPeriod==="quarterly" ? ` — ${gstQuarter} ${gstYear}` : ""}
                  {gstPeriod==="yearly" ? ` — FY ${gstYear}-${String(parseInt(gstYear)+1).slice(2)}` : ""}
                </div>
              </div>

              {/* KPI strip */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:10, marginBottom:14 }}>
                {[
                  { label:"Orders",          value:gstRows.length,                        color:"#b5003c" },
                  { label:"Seller Price",     value:INR(gstTotals.invoiceValue),           color:"#2563eb" },
                  { label:"Taxable Value",    value:INR(gstTotals.taxable),                color:"#15803d" },
                  { label:"IGST @18%",        value:INR(gstTotals.igst),                   color:"#7e22ce" },
                  { label:"CGST @9%",         value:INR(gstTotals.cgst),                   color:"#d97706" },
                  { label:"Total Output GST", value:INR(gstTotals.outputGST),              color:"#dc2626" },
                ].map(k=>(
                  <div key={k.label} style={{ ...S.kpi(k.color) }}>
                    <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                    <div style={{ fontSize:17, fontWeight:700, color:k.color, marginTop:2 }}>{k.value}</div>
                  </div>
                ))}
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:14 }}>
                {/* Order-wise GSTR-1 table */}
                <div style={{ ...S.card, overflow:"hidden" }}>
                  <div style={S.hdr}><span>📋 Order-wise GSTR-1</span><span style={{ fontSize:11, color:"#9ca3af" }}>HSN: {HSN_CODE} · {GST_RATE}% GST</span></div>
                  <div style={{ overflowX:"auto", maxHeight:450, overflowY:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead style={{ position:"sticky", top:0 }}>
                        <tr>{["#","Invoice No","Date","Order ID","SKU","Buyer State","Taxable","CGST","SGST","IGST","Total GST"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {gstRows.map((r,i)=>(
                          <tr key={r.orderId+i} style={{ background:i%2?"#fafafa":"#fff" }}>
                            <td style={{ ...S.td, color:"#9ca3af", fontSize:10 }}>{r.idx}</td>
                            <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#b5003c" }}>{r.invoiceNo}</td>
                            <td style={{ ...S.td, fontSize:10 }}>{r.date}</td>
                            <td style={{ ...S.td, fontFamily:"monospace", fontSize:10 }}>{r.orderId}</td>
                            <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{r.sku}</td>
                            <td style={S.td}>
                              <span style={{ fontSize:10 }}>{r.stateName}</span>
                              <span style={{ fontSize:9, color:"#9ca3af", marginLeft:4 }}>({r.stateCode})</span>
                              {!r.isIntra && <span style={{ fontSize:8, background:"#eff6ff", color:"#2563eb", padding:"0 4px", borderRadius:4, marginLeft:4 }}>IGST</span>}
                              {r.isIntra  && <span style={{ fontSize:8, background:"#f0fdf4", color:"#15803d", padding:"0 4px", borderRadius:4, marginLeft:4 }}>CGST+SGST</span>}
                            </td>
                            <td style={{ ...S.td, textAlign:"right", fontWeight:500 }}>{INR(r.taxable)}</td>
                            <td style={{ ...S.td, textAlign:"right", color:"#7e22ce" }}>{r.cgst>0?INR(r.cgst):"—"}</td>
                            <td style={{ ...S.td, textAlign:"right", color:"#7e22ce" }}>{r.sgst>0?INR(r.sgst):"—"}</td>
                            <td style={{ ...S.td, textAlign:"right", color:"#2563eb" }}>{r.igst>0?INR(r.igst):"—"}</td>
                            <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#dc2626" }}>{INR(r.outputGST)}</td>
                          </tr>
                        ))}
                        {gstRows.length===0 && <tr><td colSpan={11} style={{ ...S.td, textAlign:"center", padding:30, color:"#9ca3af" }}>No orders for selected period</td></tr>}
                      </tbody>
                      {gstRows.length>0 && (
                        <tfoot>
                          <tr style={{ background:"#fff0f5", fontWeight:700, position:"sticky", bottom:0 }}>
                            <td colSpan={6} style={{ ...S.td, fontWeight:700, color:"#b5003c" }}>TOTAL — {gstRows.length} orders</td>
                            <td style={{ ...S.td, textAlign:"right", fontWeight:700 }}>{INR(gstTotals.taxable)}</td>
                            <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#7e22ce" }}>{INR(gstTotals.cgst)}</td>
                            <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#7e22ce" }}>{INR(gstTotals.sgst)}</td>
                            <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#2563eb" }}>{INR(gstTotals.igst)}</td>
                            <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#dc2626" }}>{INR(gstTotals.outputGST)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>

                {/* Right panel */}
                <div>
                  {/* Seller info card */}
                  <div style={{ ...S.card, marginBottom:12 }}>
                    <div style={S.hdr}><span>🏪 Seller Details</span></div>
                    <div style={{ padding:14 }}>
                      {[
                        { label:"Name",          value:SELLER_NAME },
                        { label:"GSTIN",         value:SELLER_GSTIN },
                        { label:"State",         value:"Uttar Pradesh (09)" },
                        { label:"HSN Code",      value:HSN_CODE },
                        { label:"GST Rate",      value:GST_RATE+"%" },
                        { label:"Registration",  value:"Regular" },
                      ].map(r=>(
                        <div key={r.label} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #f9fafb", fontSize:11 }}>
                          <span style={{ color:"#9ca3af", fontWeight:600 }}>{r.label}</span>
                          <span style={{ fontWeight:500, textAlign:"right", fontSize:11 }}>{r.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* State-wise summary */}
                  <div style={S.card}>
                    <div style={S.hdr}><span>🗺 State-wise GST</span></div>
                    <div style={{ padding:"4px 0", maxHeight:280, overflowY:"auto" }}>
                      {Object.values(stateGST).sort((a,b)=>b.total-a.total).map(r=>(
                        <div key={r.code} style={{ padding:"8px 14px", borderBottom:"1px solid #f9fafb" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:3 }}>
                            <span style={{ fontWeight:600 }}>{r.state} <span style={{ fontWeight:400, color:"#9ca3af" }}>({r.code})</span></span>
                            <span style={{ fontWeight:700, color:"#dc2626" }}>{INR(r.total)}</span>
                          </div>
                          <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:"#9ca3af" }}>
                            <span>{r.orders} orders · Taxable {INR(r.taxable)}</span>
                            <span>{r.igst>0?"IGST":"CGST+SGST"}</span>
                          </div>
                        </div>
                      ))}
                      {Object.keys(stateGST).length===0 && <div style={{ padding:20, textAlign:"center", color:"#9ca3af", fontSize:12 }}>No data for this period</div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Monthly breakdown for quarterly/yearly */}
              {(gstPeriod==="quarterly"||gstPeriod==="yearly") && Object.keys(monthlyGST).length>0 && (
                <div style={{ ...S.card, marginTop:12, overflow:"hidden" }}>
                  <div style={S.hdr}><span>📅 Month-wise Breakdown</span></div>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>{["Month","Orders","Taxable Value","CGST","SGST","IGST","Total GST"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {Object.values(monthlyGST).sort((a,b)=>a.month.localeCompare(b.month)).map((r,i)=>(
                        <tr key={r.month} style={{ background:i%2?"#fafafa":"#fff" }}>
                          <td style={{ ...S.td, fontWeight:600 }}>{r.month}</td>
                          <td style={{ ...S.td, textAlign:"center" }}>{r.orders}</td>
                          <td style={{ ...S.td, textAlign:"right" }}>{INR(r.taxable)}</td>
                          <td style={{ ...S.td, textAlign:"right", color:"#7e22ce" }}>{r.cgst>0?INR(r.cgst):"—"}</td>
                          <td style={{ ...S.td, textAlign:"right", color:"#7e22ce" }}>{r.sgst>0?INR(r.sgst):"—"}</td>
                          <td style={{ ...S.td, textAlign:"right", color:"#2563eb" }}>{r.igst>0?INR(r.igst):"—"}</td>
                          <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#dc2626" }}>{INR(r.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ marginTop:12, background:"#fffbeb", borderRadius:8, padding:"10px 14px", fontSize:11, color:"#92400e", border:"1px solid #fcd34d" }}>
                ⚠ <strong>Important:</strong> GST is calculated on Seller Price (your earnings from Myntra), not the customer-paid amount. 
                For inter-state deliveries (most orders), IGST applies. For UP → UP orders, CGST+SGST applies. 
                Always verify against your Myntra payment statement before filing. HSN 42033000.
              </div>
            </div>
          )}
        </div>
      )}

            {/* ══ ADS & P&L ══ */}
      {tab === "ads" && (() => {
        const INR2 = v => "₹" + (Math.round(Number(v)||0)).toLocaleString("en-IN");
        const pf   = periodFilter;

        // Period-filtered orders
        const pOrders = pf==="all" ? orders.filter(o=>o.status==="C")
                      : orders.filter(o=>o.status==="C" && o.createdAt?.slice(0,7)===pf);

        // Aggregated revenue, GT, seller price
        const totalSellerRev = pOrders.reduce((a,o)=>a+o.sellerPrice,0);
        const totalGT        = pOrders.reduce((a,o)=>a+o.gtCharges,0);
        const totalUnits     = pOrders.length;

        // Total ad spend (all ads — not period-filtered since ad report has no date)
        const totalAdSpend   = ads.reduce((a,r)=>a+r.spend,0);
        const totalAdUnits   = ads.reduce((a,r)=>a+r.unitsTotal,0);
        const totalAdRev     = ads.reduce((a,r)=>a+r.revenueTotal,0);
        const totalImpressions = ads.reduce((a,r)=>a+r.impressions,0);
        const totalClicks    = ads.reduce((a,r)=>a+r.clicks,0);
        const avgCpc         = totalClicks>0 ? totalAdSpend/totalClicks : 0;
        const acos           = totalAdRev>0 ? (totalAdSpend/totalAdRev)*100 : 0;
        const roas           = totalAdSpend>0 ? totalAdRev/totalAdSpend : 0;

        // Cost of Goods (sum from COG defaults × units)
        const totalCOG = pOrders.reduce((a,o)=>{
          const sku  = o.sku;
          const cost = cogDefaults[sku]?.cost || 0;
          return a + cost;
        },0);

        // Additional costs for this period
        const filteredExtra = additionalCosts.filter(e=>!e.period||e.period===pf||pf==="all");

        // Each cost item is either "per_order" (₹X per shipment) or "monthly" (fixed monthly amount)
        // per_order: multiply by actual orders for total
        // monthly: fixed lump sum regardless of orders
        const perOrderExtra = filteredExtra.reduce((a,e) => {
          if ((e.amountType||"per_order") === "per_order") {
            return a + (parseFloat(e.amount)||0);          // ₹X per order
          } else {
            // monthly: convert to per-order rate
            return a + (totalUnits > 0 ? (parseFloat(e.amount)||0) / totalUnits : 0);
          }
        }, 0);
        const totalExtra = Math.round(perOrderExtra * totalUnits * 100) / 100;

        // Extra cost breakdown by category
        const extraByCategory = {};
        filteredExtra.forEach(e => {
          const cat = e.category || "Other";
          const amt = (e.amountType||"per_order")==="per_order"
            ? (parseFloat(e.amount)||0) * totalUnits
            : (parseFloat(e.amount)||0);
          extraByCategory[cat] = (extraByCategory[cat]||0) + amt;
        });

        // Full P&L
        const netRevenue     = totalSellerRev;
        const grossProfit    = netRevenue - totalGT - totalAdSpend - totalCOG - totalExtra;
        const margin         = netRevenue>0 ? (grossProfit/netRevenue*100) : 0;

        // Per-unit economics
        const perUnitRevenue = totalUnits>0 ? netRevenue/totalUnits : 0;
        const perUnitCOG     = totalUnits>0 ? totalCOG/totalUnits : 0;
        const perUnitGT      = totalUnits>0 ? totalGT/totalUnits : 0;
        const perUnitAds     = totalUnits>0 ? totalAdSpend/totalUnits : 0;
        const perUnitExtra   = Math.round(perOrderExtra * 100) / 100;
        const perUnitProfit  = perUnitRevenue - perUnitCOG - perUnitGT - perUnitAds - perUnitExtra;

        // Campaign breakdown
        const campMap = {};
        ads.forEach(r => {
          if (!campMap[r.campaignName]) campMap[r.campaignName] = { name:r.campaignName, spend:0, impressions:0, clicks:0, units:0, revenue:0, status:r.status };
          campMap[r.campaignName].spend       += r.spend;
          campMap[r.campaignName].impressions += r.impressions;
          campMap[r.campaignName].clicks      += r.clicks;
          campMap[r.campaignName].units       += r.unitsTotal;
          campMap[r.campaignName].revenue     += r.revenueTotal;
        });
        const campList = Object.values(campMap).sort((a,b)=>b.spend-a.spend);

        // ── Scale / Watch / Kill classification ──
        // Break-even ROAS ~3x (after Myntra fees ~25% + COGS + GST). Tune as needed.
        const BREAKEVEN_ROAS = 3;
        const classify = (r) => {
          const roas = r.spend > 0 ? r.revenue / r.spend : 0;
          if (r.spend >= 20 && r.revenue === 0)       return { tag:"KILL",  color:"#dc2626", reason:"Spent, zero sales" };
          if (r.spend >= 50 && roas < BREAKEVEN_ROAS) return { tag:"KILL",  color:"#dc2626", reason:`Below break-even (${roas.toFixed(1)}x)` };
          if (roas >= BREAKEVEN_ROAS * 1.6)           return { tag:"SCALE", color:"#15803d", reason:`Strong ${roas.toFixed(1)}x — increase budget` };
          if (roas >= BREAKEVEN_ROAS)                 return { tag:"WATCH", color:"#d97706", reason:`Profitable ${roas.toFixed(1)}x` };
          return { tag:"WATCH", color:"#d97706", reason:"Low volume" };
        };
        const campScored = campList.map(r => ({ ...r, ...classify(r), roas: r.spend>0?r.revenue/r.spend:0 }));
        const scaleList = campScored.filter(x=>x.tag==="SCALE").sort((a,b)=>b.roas-a.roas);
        const killList  = campScored.filter(x=>x.tag==="KILL").sort((a,b)=>b.spend-a.spend);
        const watchList = campScored.filter(x=>x.tag==="WATCH");
        const wastedSpend = campScored.filter(x=>x.revenue===0 && x.spend>=20).reduce((a,x)=>a+x.spend,0);
        const liveCount = campScored.filter(x=>(x.status||"").toUpperCase().includes("LIVE")).length;

        // Product ad breakdown
        const prodAdMap = {};
        ads.forEach(r => {
          if (!prodAdMap[r.productId]) prodAdMap[r.productId] = { id:r.productId, name:r.productName, spend:0, units:0, revenue:0 };
          prodAdMap[r.productId].spend   += r.spend;
          prodAdMap[r.productId].units   += r.unitsTotal;
          prodAdMap[r.productId].revenue += r.revenueTotal;
        });
        const prodAdList = Object.values(prodAdMap).sort((a,b)=>b.spend-a.spend);

        const EXTRA_CATS = ["Packaging","Shipping","Returns","Platform Fee","Salaries","Warehouse","Other"];
        const statusColor = { LIVE:"#15803d", CAMPAIGN_EXPIRED:"#9ca3af", CAMPAIGN_BUDGET_EXHAUSTED:"#d97706", ABORTED:"#dc2626" };

        return (
          <div>
            {/* KPI strip */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:14 }}>
              {[
                { label:"Seller Revenue",  value:INR2(netRevenue),   color:"#15803d" },
                { label:"Total Ad Spend",  value:INR2(totalAdSpend), color:"#b5003c" },
                { label:"Cost of Goods",   value:INR2(totalCOG),     color:"#d97706" },
                { label:"Net Profit",      value:INR2(grossProfit),  color:grossProfit>=0?"#15803d":"#dc2626" },
                { label:"Net Margin",      value:Math.round(margin)+"%", color:margin>=0?"#15803d":"#dc2626" },
              ].map(k=>(
                <div key={k.label} style={{ ...S.kpi(k.color) }}>
                  <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                  <div style={{ fontSize:18, fontWeight:700, color:k.color, marginTop:2 }}>{k.value}</div>
                </div>
              ))}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
              {/* Full P&L */}
              <div style={S.card}>
                <div style={S.hdr}><span>💰 Full P&L Statement</span><span style={{ fontSize:10, color:"#9ca3af", fontWeight:400 }}>{pOrders.length} orders{pf!=="all"?" · "+pf:""}</span></div>
                <div style={{ padding:14 }}>
                  {[
                    { label:"Seller Revenue (from Myntra)",     value:netRevenue,           color:"#15803d", bold:false },
                    { label:"Less: GT Charges / Commission",    value:-totalGT,             color:"#dc2626", bold:false },
                    { label:"Less: Ad Spend (PLA/Search)",      value:-totalAdSpend,        color:"#b5003c", bold:false },
                    { label:"Less: Cost of Goods",              value:-totalCOG,            color:"#d97706", bold:false },
                    { label:`Less: Additional Costs (₹${Math.round(perOrderExtra*100)/100}/order × ${totalUnits} orders = ₹${Math.round(totalExtra)})`, value:-totalExtra, color:"#6b7280", bold:false },
                    { label:"",                                 value:null },
                    { label:"Net Profit / Loss",                value:grossProfit,          color:grossProfit>=0?"#15803d":"#dc2626", bold:true },
                    { label:"Net Margin %",                     value:Math.round(margin)+"%", color:margin>=0?"#15803d":"#dc2626", bold:false, isText:true },
                  ].map((r,i)=>{
                    if (!r.label&&r.value===null) return <div key={i} style={{ height:8 }}/>;
                    return (
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:r.bold?"9px 0":"5px 0", borderBottom:r.bold?"2px solid #e5e7eb":"1px solid #f9fafb" }}>
                        <span style={{ fontSize:12, fontWeight:r.bold?700:400, color:r.bold?"#1A3A5C":"#6b7280" }}>{r.label}</span>
                        <span style={{ fontSize:12, fontWeight:r.bold?700:500, color:r.color }}>{r.isText?r.value:INR2(Math.abs(r.value||0))}{!r.isText&&(r.value||0)<0?" (Dr)":""}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Per-unit economics */}
              <div>
                <div style={S.card}>
                  <div style={S.hdr}><span>📦 Per-Unit Economics</span></div>
                  <div style={{ padding:14 }}>
                    {[
                      { label:"Avg Selling Price",   value:perUnitRevenue, color:"#15803d" },
                      { label:"Cost of Goods",        value:-perUnitCOG,   color:"#d97706" },
                      { label:"GT Charges",           value:-perUnitGT,    color:"#dc2626" },
                      { label:"Ad Spend per Unit",    value:-perUnitAds,   color:"#b5003c" },
                      { label:"Additional Costs / order", value:-perUnitExtra, color:"#6b7280" },
                      { label:"Net Profit per Unit",  value:perUnitProfit, color:perUnitProfit>=0?"#15803d":"#dc2626", bold:true },
                    ].map((r,i)=>(
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:r.bold?"2px solid #e5e7eb":"1px solid #f9fafb" }}>
                        <span style={{ fontSize:12, color:"#6b7280", fontWeight:r.bold?700:400 }}>{r.label}</span>
                        <span style={{ fontSize:12, fontWeight:r.bold?700:500, color:r.color }}>{INR2(Math.abs(r.value||0))}{r.value<0?" (Dr)":""}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ad metrics */}
                <div style={S.card}>
                  <div style={S.hdr}><span>📢 Ad Metrics</span></div>
                  <div style={{ padding:14 }}>
                    {[
                      { label:"Total Impressions", value:totalImpressions.toLocaleString("en-IN"), isText:true },
                      { label:"Total Clicks",      value:totalClicks.toLocaleString("en-IN"), isText:true },
                      { label:"Avg CTR",           value:(totalImpressions>0?(totalClicks/totalImpressions*100).toFixed(2):0)+"%", isText:true },
                      { label:"Avg CPC",           value:INR2(avgCpc), isText:true },
                      { label:"Ad Units Sold",     value:totalAdUnits, isText:true },
                      { label:"Ad Revenue",        value:INR2(totalAdRev), isText:true },
                      { label:"ACOS",              value:acos.toFixed(1)+"%", isText:true, color:acos<30?"#15803d":"#dc2626" },
                      { label:"ROAS",              value:roas.toFixed(2)+"x", isText:true, color:roas>=3?"#15803d":"#d97706" },
                    ].map((r,i)=>(
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #f9fafb", fontSize:12 }}>
                        <span style={{ color:"#6b7280" }}>{r.label}</span>
                        <span style={{ fontWeight:600, color:r.color||"#374151" }}>{r.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* COG + Additional costs management */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
              {/* Cost of Goods */}
              <div style={S.card}>
                <div style={S.hdr}>
                  <span>🏷️ Cost of Goods (per SKU)</span>
                  <div style={{ display:"flex", gap:6 }}>
                    <button style={{ ...S.btnOut, fontSize:11 }} onClick={downloadCogTemplate}>⬇ Template</button>
                    <button style={{ ...S.btn("#2563eb"), fontSize:11 }} onClick={()=>{ setShowCogUpload(v=>!v); setShowCogForm(false); }}>📂 Upload Excel</button>
                    <button style={S.btn()} onClick={()=>{ setShowCogForm(v=>!v); setShowCogUpload(false); }}>+ Manual</button>
                  </div>
                </div>
                {/* Excel / CSV upload panel */}
                {showCogUpload && (
                  <div style={{ padding:"12px 16px", background:"#eff6ff", borderBottom:"1px solid #bfdbfe" }}>
                    <div style={{ fontWeight:600, fontSize:12, color:"#1e40af", marginBottom:8 }}>📂 Upload COG Excel / CSV</div>
                    <div style={{ fontSize:11, color:"#1e40af", marginBottom:10 }}>
                      File format: <strong>SKU, Product Name, Cost per Unit</strong> — one row per SKU. First row can be header.<br/>
                      Supports .csv files. For Excel (.xlsx), save as CSV first.
                    </div>
                    <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                      <input ref={cogExcelRef} type="file" accept=".csv,.txt" style={{ display:"none" }}
                        onChange={e=>{ handleCogUpload(e.target.files[0]); e.target.value=""; }}/>
                      <button style={{ ...S.btn("#2563eb"), fontSize:12, padding:"8px 20px" }}
                        onClick={()=>cogExcelRef.current?.click()}>
                        📂 Choose CSV File
                      </button>
                      <button style={{ ...S.btn("#15803d"), fontSize:12, padding:"8px 16px" }}
                        onClick={downloadCogTemplate}>
                        ⬇ Download Template
                      </button>
                      <button style={{ ...S.btnOut, fontSize:11 }} onClick={()=>setShowCogUpload(false)}>Cancel</button>
                    </div>
                    <div style={{ marginTop:10, background:"#fff", borderRadius:6, padding:"8px 12px", fontSize:11, color:"#6b7280", fontFamily:"monospace" }}>
                      <div style={{ fontWeight:600, color:"#374151", marginBottom:4 }}>Expected format:</div>
                      <div>SKU,Product Name,Cost per Unit</div>
                      <div>AT_03_Black_36,Autolock Belt Black 36,280</div>
                      <div>Rev_01_Brown_34,Reversible Belt Brown,300</div>
                      <div>Br_01_Tan_34,Classic Belt Tan,250</div>
                    </div>
                    {Object.keys(cogDefaults).length > 0 && (
                      <div style={{ marginTop:8, fontSize:11, color:"#1e40af" }}>
                        ℹ️ Uploading will merge with existing {Object.keys(cogDefaults).length} SKUs. Same SKU = cost gets updated.
                      </div>
                    )}
                  </div>
                )}
                {showCogForm && (
                  <div style={{ padding:"14px 16px", background:"#f9fafb", borderBottom:"1px solid #e5e7eb" }}>
                    <div style={{ fontSize:11, color:"#6b7280", marginBottom:10 }}>
                      Add cost of goods for multiple SKUs at once. Set a shared cost or individual costs per SKU.
                    </div>

                    {/* Shared cost input */}
                    <div style={{ display:"flex", gap:10, alignItems:"flex-end", marginBottom:12 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>Cost per Unit ₹ (applies to all selected)</div>
                        <input type="number" style={{ padding:"7px 10px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", width:"100%" }}
                          placeholder="e.g. 350" value={cogForm.costPerUnit}
                          onChange={e=>{
                            const v = e.target.value;
                            setCogForm(f=>({...f, costPerUnit:v}));
                            // Apply to all rows that don't have individual cost
                            setCogForm(f=>({...f, costPerUnit:v,
                              rows:(f.rows||[]).map(r=>r.useShared===false?r:{...r,cost:v})
                            }));
                          }}/>
                      </div>
                      <button style={{ ...S.btn("#2563eb"), fontSize:11, padding:"7px 14px", whiteSpace:"nowrap" }}
                        onClick={()=>{
                          const allSkus = [
                            ...products.filter(p=>p.shopifySku||p.sku).map(p=>({ sku:p.shopifySku||p.sku, name:p.name, source:"Shopify" })),
                            ...[...new Map(orders.filter(o=>o.sku).map(o=>[o.sku,o])).values()].map(o=>({ sku:o.sku, name:o.productName, source:"Myntra" })),
                            ...myntraSkus.map(s=>({ sku:s.sku, name:s.name||s.sku, source:"SKU List" })),
                          ].filter((x,i,arr)=>x.sku&&arr.findIndex(y=>y.sku===x.sku)===i);
                          const addedSet = new Set((cogForm.rows||[]).map(r=>r.sku));
                          const newRows  = allSkus.filter(s=>!addedSet.has(s.sku)).map(s=>({ sku:s.sku, name:s.name, source:s.source, cost:cogForm.costPerUnit||"", selected:true }));
                          setCogForm(f=>({...f, rows:[...(f.rows||[]),...newRows]}));
                        }}>
                        + Add All SKUs
                      </button>
                    </div>

                    {/* SKU rows */}
                    <div style={{ border:"1px solid #e5e7eb", borderRadius:8, overflow:"hidden", marginBottom:10 }}>
                      {/* Search + add parent SKU */}
                    {(() => {
                      const addedSkus = new Set((cogForm.rows||[]).map(r=>r.sku));
                      const allSkus = [
                        ...products.filter(p=>p.shopifySku||p.sku).map(p=>({ sku:p.shopifySku||p.sku, name:p.name, source:"Shopify" })),
                        ...[...new Map(orders.filter(o=>o.sku).map(o=>[o.sku,o])).values()].map(o=>({ sku:o.sku, name:o.productName, source:"Myntra" })),
                        ...myntraSkus.map(s=>({ sku:s.sku, name:s.name||s.sku, source:"SKU List" })),
                      ].filter((x,i2,arr)=>x.sku&&arr.findIndex(y=>y.sku===x.sku)===i2)
                       .filter(s=>!addedSkus.has(s.sku));
                      const q = skuSearch.toLowerCase();
                      const filtered = q ? allSkus.filter(s=>s.sku.toLowerCase().includes(q)||s.name.toLowerCase().includes(q)) : allSkus;
                      const addRow = (p) => {
                        setCogForm(f=>({ ...f, rows:[...(f.rows||[]), { sku:p.sku, name:p.name, source:p.source, cost:f.costPerUnit||"", selected:true }] }));
                        setSkuSearch("");
                      };
                      return (
                        <div style={{ padding:"8px 12px", background:"#fff", borderBottom:"1px solid #f3f4f6" }}>
                          <div style={{ display:"flex", gap:8, marginBottom:6 }}>
                            <div style={{ position:"relative", flex:1 }}>
                              <span style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", color:"#9ca3af" }}>🔍</span>
                              <input style={{ width:"100%", padding:"6px 10px 6px 28px", borderRadius:6, border:"1px solid #d1d5db", fontSize:11, fontFamily:"inherit", outline:"none" }}
                                placeholder={`Search SKU or product name… (${allSkus.length} available)`}
                                value={skuSearch}
                                onChange={e=>setSkuSearch(e.target.value)}/>
                            </div>
                            <input style={{ width:140, padding:"6px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:11, fontFamily:"inherit" }}
                              placeholder="Manual SKU + Enter"
                              onKeyDown={e=>{ if(e.key==="Enter"&&e.target.value.trim()){ addRow({sku:e.target.value.trim(),name:"",source:"Manual"}); e.target.value=""; }}}/>
                          </div>
                          {skuSearch && (
                            <div style={{ display:"flex", flexWrap:"wrap", gap:5, maxHeight:110, overflowY:"auto", padding:"4px 0" }}>
                              {filtered.length===0
                                ? <span style={{ fontSize:11, color:"#9ca3af", padding:"4px 0" }}>No matching SKUs — use manual entry on the right</span>
                                : filtered.slice(0,20).map(p=>(
                                    <button key={p.sku} onClick={()=>addRow(p)}
                                      style={{ padding:"3px 10px", borderRadius:20, fontSize:11, cursor:"pointer", border:"1px solid #e5e7eb", background:"#fff", fontFamily:"inherit", color:"#374151", display:"flex", alignItems:"center", gap:5, transition:"border-color .15s" }}
                                      onMouseEnter={e=>e.currentTarget.style.borderColor="#F47920"}
                                      onMouseLeave={e=>e.currentTarget.style.borderColor="#e5e7eb"}>
                                      <span style={{ fontWeight:700, color:"#F47920", fontFamily:"monospace" }}>{p.sku}</span>
                                      {p.name&&<span style={{ color:"#9ca3af", fontSize:10 }}>· {p.name.slice(0,25)}</span>}
                                      <span style={{ color:"#15803d", fontWeight:700 }}>+</span>
                                    </button>
                                  ))
                              }
                            </div>
                          )}
                        </div>
                      );
                    })()}

                      {/* SKU rows list */}
                      {(cogForm.rows||[]).length===0 ? (
                        <div style={{ padding:"16px", textAlign:"center", fontSize:11, color:"#9ca3af" }}>
                          Select SKUs from dropdown or click "+ Add All SKUs"
                        </div>
                      ) : (
                        <div style={{ maxHeight:240, overflowY:"auto" }}>
                          {/* Header */}
                          <div style={{ display:"grid", gridTemplateColumns:"auto 1fr auto 100px auto", gap:8, padding:"5px 12px", background:"#f9fafb", fontSize:10, fontWeight:600, color:"#9ca3af", borderBottom:"1px solid #f3f4f6" }}>
                            <span>✓</span><span>Product / SKU</span><span>Source</span><span>Cost ₹/unit</span><span></span>
                          </div>
                          {(cogForm.rows||[]).map((row,i)=>(
                            <div key={row.sku} style={{ display:"grid", gridTemplateColumns:"auto 1fr auto 100px auto", gap:8, padding:"6px 12px", alignItems:"center", borderBottom:"1px solid #f9fafb", background:i%2?"#fafafa":"#fff" }}>
                              <input type="checkbox" checked={row.selected!==false} style={{ accentColor:"#F47920" }}
                                onChange={e=>setCogForm(f=>({...f, rows:f.rows.map((r,j)=>j===i?{...r,selected:e.target.checked}:r)}))}/>
                              <div>
                                <div style={{ fontSize:11, fontWeight:500 }}>{row.name||row.sku}</div>
                                <div style={{ fontSize:10, color:"#F47920", fontFamily:"monospace" }}>{row.sku}</div>
                              </div>
                              <span style={{ fontSize:9, padding:"1px 6px", borderRadius:8, background:"#f3f4f6", color:"#6b7280" }}>{row.source}</span>
                              <input type="number" style={{ padding:"4px 6px", borderRadius:5, border:"1px solid #d1d5db", fontSize:11, fontFamily:"inherit", width:"100%" }}
                                placeholder={cogForm.costPerUnit||"₹"}
                                value={row.cost}
                                onChange={e=>setCogForm(f=>({...f, rows:f.rows.map((r,j)=>j===i?{...r,cost:e.target.value}:r)}))}/>
                              <button style={{ background:"none", border:"none", color:"#dc2626", cursor:"pointer", fontSize:16, lineHeight:1 }}
                                onClick={()=>setCogForm(f=>({...f, rows:f.rows.filter((_,j)=>j!==i)}))}>×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      <button style={{ ...S.btn(), fontSize:12, padding:"7px 18px" }} onClick={()=>{
                        const rows = (cogForm.rows||[]).filter(r=>r.selected!==false&&r.sku);
                        if (!rows.length) { toast("Select at least one SKU","error"); return; }
                        const updated = { ...cogDefaults };
                        rows.forEach(r=>{ updated[r.sku]={ name:r.name||r.sku, cost:parseFloat(r.cost||cogForm.costPerUnit||0) }; });
                        setCogDefaults(updated);
                        localStorage.setItem("3june_myntra_cog", JSON.stringify(updated));
                        setCogForm({ costPerUnit:"", rows:[] });
                        setShowCogForm(false);
                        toast(`✅ Cost saved for ${rows.length} SKU${rows.length>1?"s":""}`, "success");
                      }}>
                        💾 Save {(cogForm.rows||[]).filter(r=>r.selected!==false).length || ""} SKUs
                      </button>
                      <button style={{ ...S.btnOut, fontSize:11 }} onClick={()=>{ setCogForm({ costPerUnit:"", rows:[] }); setShowCogForm(false); }}>Cancel</button>
                      {(cogForm.rows||[]).length>0 && (
                        <span style={{ fontSize:11, color:"#9ca3af", marginLeft:4 }}>
                          {(cogForm.rows||[]).filter(r=>r.selected!==false).length} of {(cogForm.rows||[]).length} selected
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div style={{ maxHeight:220, overflowY:"auto" }}>
                  {Object.keys(cogDefaults).length===0 ? (
                    <div style={{ padding:20, textAlign:"center", color:"#9ca3af", fontSize:12 }}>
                      Add cost per unit for each SKU to calculate accurate profit
                    </div>
                  ) : Object.entries(cogDefaults).map(([sku,v],i)=>(
                    <div key={sku} style={{ padding:"8px 14px", borderBottom:"1px solid #f3f4f6", display:"flex", justifyContent:"space-between", alignItems:"center", background:i%2?"#fafafa":"#fff" }}>
                      <div>
                        <div style={{ fontFamily:"monospace", fontSize:11, color:"#F47920", fontWeight:600 }}>{sku}</div>
                        <div style={{ fontSize:11, color:"#6b7280" }}>{v.name||"—"}</div>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontWeight:700, fontSize:13, color:"#d97706" }}>{INR2(v.cost)}/unit</span>
                        <button style={{ background:"none", border:"none", color:"#dc2626", cursor:"pointer", fontSize:14 }}
                          onClick={()=>{ const u={...cogDefaults}; delete u[sku]; setCogDefaults(u); localStorage.setItem("3june_myntra_cog",JSON.stringify(u)); }}>×</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional costs */}
              <div style={S.card}>
                <div style={S.hdr}>
                  <span>➕ Additional Costs</span>
                  <button style={S.btn()} onClick={()=>setShowExtraForm(v=>!v)}>+ Add</button>
                </div>
                {showExtraForm && (
                  <div style={{ padding:"12px 14px", background:"#f9fafb", borderBottom:"1px solid #e5e7eb" }}>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:8, marginBottom:8 }}>
                      <div>
                        <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>Label *</div>
                        <input style={{ padding:"6px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:11, fontFamily:"inherit", width:"100%" }}
                          placeholder="Bubble wrap" value={extraForm.label} onChange={e=>setExtraForm(f=>({...f,label:e.target.value}))}/>
                      </div>
                      <div>
                        <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>Amount ₹ *</div>
                        <input type="number" style={{ padding:"6px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:11, fontFamily:"inherit", width:"100%" }}
                          placeholder={extraForm.amountType==="per_order"?"e.g. 18":"e.g. 2000"} value={extraForm.amount} onChange={e=>setExtraForm(f=>({...f,amount:e.target.value}))}/>
                      </div>
                      <div>
                        <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>Cost Type *</div>
                        <select style={{ padding:"6px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:11, fontFamily:"inherit", width:"100%" }}
                          value={extraForm.amountType} onChange={e=>setExtraForm(f=>({...f,amountType:e.target.value}))}>
                          <option value="per_order">Per Order (₹X per shipment)</option>
                          <option value="monthly">Monthly Fixed (total ÷ orders)</option>
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>Period (YYYY-MM)</div>
                        <input style={{ padding:"6px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:11, fontFamily:"inherit", width:"100%" }}
                          placeholder="2026-04 or leave blank" value={extraForm.period} onChange={e=>setExtraForm(f=>({...f,period:e.target.value}))}/>
                      </div>
                      <div>
                        <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>Category</div>
                        <select style={{ padding:"6px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:11, fontFamily:"inherit", width:"100%" }}
                          value={extraForm.category} onChange={e=>setExtraForm(f=>({...f,category:e.target.value}))}>
                          {EXTRA_CATS.map(c=><option key={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:6 }}>
                      <button style={{ ...S.btn(), fontSize:11, padding:"5px 12px" }} onClick={saveExtra}>Save</button>
                      <button style={{ ...S.btnOut, fontSize:11 }} onClick={()=>setShowExtraForm(false)}>Cancel</button>
                    </div>
                  </div>
                )}
                <div style={{ maxHeight:220, overflowY:"auto" }}>
                  {/* Per-order summary */}
                  {filteredExtra.length > 0 && totalUnits > 0 && (() => {
                    const perO = filteredExtra.reduce((a,e)=>{
                      if ((e.amountType||"per_order")==="per_order") return a+(parseFloat(e.amount)||0);
                      return a+(totalUnits>0?(parseFloat(e.amount)||0)/totalUnits:0);
                    },0);
                    const totalEx = Math.round(perO * totalUnits * 100)/100;
                    return (
                      <div style={{ padding:"8px 14px", background:"#f0fdf4", borderTop:"1px solid #bbf7d0", fontSize:11, color:"#15803d" }}>
                        💡 <strong>₹{Math.round(perO*100)/100}/order × {totalUnits} orders = ₹{Math.round(totalEx)}</strong> total deducted from P&L
                        <div style={{ marginTop:3, color:"#6b7280" }}>
                          {filteredExtra.map((e,i)=>(
                            <span key={i} style={{ marginRight:10 }}>
                              {e.label}: {(e.amountType||"per_order")==="per_order"
                                ? `₹${e.amount}/order`
                                : `₹${e.amount}/month (÷${totalUnits}=₹${Math.round(parseFloat(e.amount||0)/totalUnits*100)/100}/order)`}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  {filteredExtra.length===0 ? (
                    <div style={{ padding:20, textAlign:"center", color:"#9ca3af", fontSize:12 }}>
                      Add packaging, shipping, salary or any other monthly costs
                    </div>
                  ) : filteredExtra.map((e,i)=>(
                    <div key={e.id} style={{ padding:"8px 14px", borderBottom:"1px solid #f3f4f6", display:"flex", justifyContent:"space-between", alignItems:"center", background:i%2?"#fafafa":"#fff" }}>
                      <div>
                        <div style={{ fontSize:12, fontWeight:500 }}>{e.label}</div>
                        <div style={{ fontSize:10, color:"#9ca3af" }}>{e.category}{e.period?" · "+e.period:" · All periods"}</div>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontWeight:700, color:"#dc2626" }}>{INR2(e.amount)}</span>
                        <button style={{ background:"none", border:"none", color:"#dc2626", cursor:"pointer", fontSize:14 }}
                          onClick={()=>{ const u=additionalCosts.filter(x=>x.id!==e.id); setAdditionalCosts(u); localStorage.setItem("3june_myntra_extra",JSON.stringify(u)); }}>×</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Campaign summary — Scale / Watch / Kill */}
            {ads.length > 0 && (
              <div style={{ marginBottom:14 }}>
                {/* Headline strip */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:12 }}>
                  {[
                    { label:"Blended ROAS", value:roas.toFixed(2)+"x", color: roas>=3?"#15803d":"#dc2626", sub:INR2(totalAdSpend)+" → "+INR2(totalAdRev) },
                    { label:"Wasted Spend", value:INR2(wastedSpend), color:"#dc2626", sub:killList.filter(x=>x.revenue===0).length+" campaigns, 0 sales" },
                    { label:"Scale These", value:scaleList.length, color:"#15803d", sub:"strong ROAS — fund more" },
                    { label:"Kill These", value:killList.length, color:"#dc2626", sub:"losing money" },
                  ].map(k=>(
                    <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"12px 14px", borderLeft:"3px solid "+k.color }}>
                      <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                      <div style={{ fontSize:22, fontWeight:700, color:k.color }}>{k.value}</div>
                      <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{k.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Action lists */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  {/* Scale */}
                  <div style={{ ...S.card, marginBottom:0 }}>
                    <div style={{ ...S.hdr, color:"#15803d" }}><span>🚀 Scale these ({scaleList.length})</span></div>
                    <div style={{ padding:"4px 0" }}>
                      {scaleList.length===0 ? <div style={{ padding:"12px 16px", fontSize:12, color:"#9ca3af" }}>No standout winners yet.</div>
                       : scaleList.slice(0,6).map((x,i)=>(
                        <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 16px", borderBottom:"1px solid #f3f4f6" }}>
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontSize:12, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{x.name}</div>
                            <div style={{ fontSize:10, color:"#9ca3af" }}>{INR2(x.spend)} → {INR2(x.revenue)} · {x.units}u</div>
                          </div>
                          <span style={{ fontSize:14, fontWeight:700, color:"#15803d", flexShrink:0, marginLeft:8 }}>{x.roas.toFixed(1)}x</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Kill */}
                  <div style={{ ...S.card, marginBottom:0 }}>
                    <div style={{ ...S.hdr, color:"#dc2626" }}><span>🛑 Pause / fix these ({killList.length})</span></div>
                    <div style={{ padding:"4px 0" }}>
                      {killList.length===0 ? <div style={{ padding:"12px 16px", fontSize:12, color:"#9ca3af" }}>Nothing bleeding money. 👍</div>
                       : killList.slice(0,6).map((x,i)=>(
                        <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 16px", borderBottom:"1px solid #f3f4f6" }}>
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontSize:12, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{x.name}</div>
                            <div style={{ fontSize:10, color:"#9ca3af" }}>{x.reason}</div>
                          </div>
                          <span style={{ fontSize:13, fontWeight:700, color:"#dc2626", flexShrink:0, marginLeft:8 }}>{INR2(x.spend)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {wastedSpend > 0 && (
                  <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#991b1b", marginTop:12 }}>
                    💸 You've spent <strong>{INR2(wastedSpend)}</strong> on campaigns that made zero sales. Pausing them would lift your blended ROAS from {roas.toFixed(2)}x to <strong>{((totalAdRev)/(totalAdSpend-wastedSpend||1)).toFixed(2)}x</strong>.
                  </div>
                )}
              </div>
            )}

            {/* Campaign performance table */}
            {ads.length > 0 && (
              <div style={{ ...S.card, overflow:"hidden", marginBottom:14 }}>
                <div style={S.hdr}>
                  <span>📢 Campaign Performance</span>
                  <span style={{ fontSize:11, color:"#9ca3af", fontWeight:400 }}>{ads.length} ad rows · {INR2(totalAdSpend)} total spend</span>
                </div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>{["Campaign","Status","Impressions","Clicks","CTR","Avg CPC","Spend","Units","Revenue","ROAS","Verdict"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {campList.map((r,i)=>{
                        const roas2 = r.spend>0 ? r.revenue/r.spend : 0;
                        const ctr2  = r.impressions>0 ? (r.clicks/r.impressions*100).toFixed(2) : "0.00";
                        const cpc2  = r.clicks>0 ? r.spend/r.clicks : 0;
                        const sc    = statusColor[r.status] || "#9ca3af";
                        return (
                          <tr key={r.name} style={{ background:i%2?"#fafafa":"#fff" }}>
                            <td style={{ ...S.td, fontWeight:500, maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.name}</td>
                            <td style={S.td}><span style={{ fontSize:9, padding:"1px 6px", borderRadius:8, background:sc+"18", color:sc, fontWeight:600 }}>{r.status.replace("CAMPAIGN_","").replace("_"," ")}</span></td>
                            <td style={{ ...S.td, textAlign:"right" }}>{r.impressions.toLocaleString("en-IN")}</td>
                            <td style={{ ...S.td, textAlign:"right" }}>{r.clicks}</td>
                            <td style={{ ...S.td, textAlign:"right" }}>{ctr2}%</td>
                            <td style={{ ...S.td, textAlign:"right" }}>{INR2(cpc2)}</td>
                            <td style={{ ...S.td, textAlign:"right", fontWeight:600, color:"#b5003c" }}>{INR2(r.spend)}</td>
                            <td style={{ ...S.td, textAlign:"center", fontWeight:600, color:"#2563eb" }}>{r.units}</td>
                            <td style={{ ...S.td, textAlign:"right", color:"#15803d" }}>{INR2(r.revenue)}</td>
                            <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:roas2>=3?"#15803d":roas2>=1?"#d97706":"#dc2626" }}>{roas2.toFixed(2)}x</td>
                            <td style={S.td}>{(() => { const v = classify(r); return <span style={{ fontSize:9, padding:"2px 7px", borderRadius:8, background:v.color+"18", color:v.color, fontWeight:700 }}>{v.tag}</span>; })()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:"#fff0f5", fontWeight:700 }}>
                        <td colSpan={6} style={{ ...S.td, fontWeight:700 }}>TOTAL</td>
                        <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#b5003c" }}>{INR2(totalAdSpend)}</td>
                        <td style={{ ...S.td, textAlign:"center", fontWeight:700 }}>{totalAdUnits}</td>
                        <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#15803d" }}>{INR2(totalAdRev)}</td>
                        <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:roas>=3?"#15803d":"#d97706" }}>{roas.toFixed(2)}x</td>
                        <td style={S.td}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Product-level ad spend */}
            {prodAdList.length > 0 && (
              <div style={{ ...S.card, overflow:"hidden" }}>
                <div style={S.hdr}><span>📦 Product-wise Ad Spend & Profit</span></div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>{["Product","Ad Spend","Ad Units","Ad Revenue","COG/unit","ROAS","Profit Est."].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {prodAdList.map((r,i)=>{
                        const roas2  = r.spend>0 ? r.revenue/r.spend : 0;
                        const cog    = cogDefaults[r.id]?.cost || 0;
                        const profEst= r.revenue - r.spend - (cog * r.units);
                        return (
                          <tr key={r.id} style={{ background:i%2?"#fafafa":"#fff" }}>
                            <td style={{ ...S.td, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontSize:11 }}>{r.name}</td>
                            <td style={{ ...S.td, textAlign:"right", fontWeight:600, color:"#b5003c" }}>{INR2(r.spend)}</td>
                            <td style={{ ...S.td, textAlign:"center" }}>{r.units}</td>
                            <td style={{ ...S.td, textAlign:"right", color:"#15803d" }}>{INR2(r.revenue)}</td>
                            <td style={{ ...S.td, textAlign:"right", color:"#d97706" }}>{cog ? INR2(cog) : <span style={{ color:"#9ca3af" }}>—</span>}</td>
                            <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:roas2>=3?"#15803d":roas2>=1?"#d97706":"#dc2626" }}>{roas2.toFixed(2)}x</td>
                            <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:profEst>=0?"#15803d":"#dc2626" }}>{INR2(profEst)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {ads.length === 0 && (
              <div style={{ ...S.card, padding:40, textAlign:"center", color:"#9ca3af" }}>
                <div style={{ fontSize:36, marginBottom:10 }}>📢</div>
                <div style={{ fontWeight:600, marginBottom:6 }}>No ads data uploaded yet</div>
                <div style={{ fontSize:12, marginBottom:14 }}>Upload your Myntra PLA/Campaign report CSV from the Upload tab</div>
                <button style={S.btn("#b5003c")} onClick={()=>setTab("upload")}>📁 Go to Upload →</button>
              </div>
            )}
          </div>
        );
      })()}

            {/* ══ META ADS INTELLIGENCE REPORT ══ */}
      {tab === "metaads" && (() => {
        const INR2 = v => "₹" + Math.round(Number(v)||0).toLocaleString("en-IN");

        // Period filter
        const pOrders = periodFilter==="all"
          ? orders.filter(o=>o.status==="C")
          : orders.filter(o=>o.status==="C" && o.createdAt?.slice(0,7)===periodFilter);

        // Parse SKU into parent / color / size
        const parseSku = (sku="") => {
          const parts     = sku.split("_");
          const size      = parts.slice().reverse().find(p=>/^\d+$/.test(p)) || "Free";
          const colorParts= parts.slice(2, parts.findIndex(p=>/^\d+$/.test(p))).filter(p=>p.length>1);
          const color     = colorParts.join(" ") || "N/A";
          return { sku, color, size };  // full SKU intact — no parent extraction
        };

        // Enrich orders
        const enriched = pOrders.map(o=>{
          const parsed = parseSku(o.sku);
          return { ...o, ...parsed,
            sizeNum: parseInt(parsed.size) || 0,
            state:   (o.state||"").trim().toUpperCase(),
            city:    (o.city||"").trim().replace(/,\s*$/,"").trim(),
          };
        });

        // ── Aggregations ──────────────────────────────────────────
        // 1. Best selling parent SKUs
        const skuMap = {};
        enriched.forEach(o=>{
          if (!skuMap[o.sku]) skuMap[o.sku] = { sku:o.sku, name:o.productName, units:0, revenue:0, colors:{}, sizes:{} };
          skuMap[o.sku].units++;
          skuMap[o.sku].revenue += o.sellerPrice;
          skuMap[o.sku].colors[o.color] = (skuMap[o.sku].colors[o.color]||0)+1;
          skuMap[o.sku].sizes[o.size]   = (skuMap[o.sku].sizes[o.size]||0)+1;
        });
        const skuList = Object.values(skuMap).sort((a,b)=>b.units-a.units);

        // 2. Best sizes
        const sizeMap = {};
        enriched.forEach(o=>{
          if (!sizeMap[o.size]) sizeMap[o.size] = { size:o.size, units:0, revenue:0 };
          sizeMap[o.size].units++;
          sizeMap[o.size].revenue += o.sellerPrice;
        });
        const sizeList = Object.values(sizeMap).sort((a,b)=>b.units-a.units);

        // 3. Best colors
        const colorMap = {};
        enriched.forEach(o=>{
          if (!o.color||o.color==="N/A") return;
          if (!colorMap[o.color]) colorMap[o.color] = { color:o.color, units:0 };
          colorMap[o.color].units++;
        });
        const colorList = Object.values(colorMap).sort((a,b)=>b.units-a.units);

        // 4. Best states
        const stateMap = {};
        enriched.forEach(o=>{
          if (!o.state) return;
          if (!stateMap[o.state]) stateMap[o.state] = { state:o.state, units:0, revenue:0 };
          stateMap[o.state].units++;
          stateMap[o.state].revenue += o.sellerPrice;
        });
        const stateList = Object.values(stateMap).sort((a,b)=>b.units-a.units);

        // 5. Best cities
        const cityMap = {};
        enriched.forEach(o=>{
          if (!o.city) return;
          if (!cityMap[o.city]) cityMap[o.city] = { city:o.city, state:o.state, units:0, revenue:0 };
          cityMap[o.city].units++;
          cityMap[o.city].revenue += o.sellerPrice;
        });
        const cityList = Object.values(cityMap).sort((a,b)=>b.units-a.units);

        // 6. Size-State heat (which sizes sell in which states)
        const sizeStateMap = {};
        enriched.forEach(o=>{
          const key = o.size+"_"+o.state;
          sizeStateMap[key] = (sizeStateMap[key]||0)+1;
        });

        const totalUnits   = enriched.length;
        const totalRevenue = enriched.reduce((a,o)=>a+o.sellerPrice,0);

        // ── State name mapping
        const STATE_NAMES2 = {
          KA:"Karnataka",MH:"Maharashtra",DL:"Delhi",UP:"Uttar Pradesh",TN:"Tamil Nadu",
          RJ:"Rajasthan",GJ:"Gujarat",WB:"West Bengal",TS:"Telangana",HR:"Haryana",
          MP:"Madhya Pradesh",PB:"Punjab",KL:"Kerala",BR:"Bihar",JH:"Jharkhand",
          AP:"Andhra Pradesh",OR:"Odisha",OD:"Odisha",SK:"Sikkim",
        };

        // ── Export functions ──────────────────────────────────────
        const exportMetaAudienceCSV = () => {
          // Format for Meta Ads audience targeting — cities and states
          const rows2 = [
            ["Type","Value","Units Sold","Revenue","% of Total","Meta Targeting Suggestion"],
            ...stateList.map(s=>["State", STATE_NAMES2[s.state]||s.state, s.units, Math.round(s.revenue), Math.round(s.units/totalUnits*100)+"%", "Add to Meta geo targeting"]),
            ["","","","","",""],
            ...cityList.map(c=>["City", c.city, c.units, Math.round(c.revenue), Math.round(c.units/totalUnits*100)+"%", "Add to Meta city targeting"]),
          ];
          const csv = rows2.map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
          const a=document.createElement("a"); a.href="data:text/csv,"+encodeURIComponent(csv); a.download=`Myntra_Meta_GeoTargeting_${periodFilter}.csv`; a.click();
          toast("Geo targeting CSV exported ✓","success");
        };

        const exportProductInsightsCSV = () => {
          const rows2 = [
            ["Parent SKU","Product Name","Units Sold","Revenue","Top Color","Top Size","Meta Ad Suggestion"],
            ...skuList.map(s=>{
              const topColor = Object.entries(s.colors).sort((a,b)=>b[1]-a[1])[0]?.[0]||"";
              const topSize  = Object.entries(s.sizes).sort((a,b)=>b[1]-a[1])[0]?.[0]||"";
              return [s.sku, s.name, s.units, Math.round(s.revenue), topColor, topSize, `Promote ${s.sku} in ${topColor} size ${topSize}`];
            }),
          ];
          const csv = rows2.map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
          const a=document.createElement("a"); a.href="data:text/csv,"+encodeURIComponent(csv); a.download=`Myntra_Meta_ProductInsights_${periodFilter}.csv`; a.click();
          toast("Product insights CSV exported ✓","success");
        };

        const printReport = () => {
          const period = periodFilter==="all"?"All Time":periodFilter;
          const html = `<!DOCTYPE html><html><head><title>Myntra → Meta Ads Report</title>
          <style>
            *{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:16px}
            h1{font-size:16px;color:#1A3A5C;margin-bottom:4px}
            h2{font-size:12px;color:#b5003c;margin:14px 0 6px;border-bottom:1px solid #eee;padding-bottom:3px}
            .meta{font-size:10px;color:#666;margin-bottom:12px}
            .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
            table{width:100%;border-collapse:collapse;margin-bottom:10px}
            th{background:#1A3A5C;color:#fff;padding:4px 8px;font-size:10px;text-align:left}
            td{padding:4px 8px;border-bottom:1px solid #f0f0f0;font-size:10px}
            .highlight{background:#fff8f2;font-weight:bold}
            .badge{background:#F47920;color:#fff;padding:1px 6px;border-radius:8px;font-size:9px}
            .tip{background:#eff6ff;border-left:3px solid #2563eb;padding:6px 10px;font-size:10px;margin-top:6px;border-radius:0 4px 4px 0}
            @media print{@page{size:A4;margin:10mm}}
          </style></head><body>
          <h1>🎯 Myntra → Meta Ads Intelligence Report</h1>
          <div class="meta">Period: <strong>${period}</strong> &nbsp;·&nbsp; Total orders: <strong>${totalUnits}</strong> &nbsp;·&nbsp; Revenue: <strong>₹${Math.round(totalRevenue).toLocaleString("en-IN")}</strong> &nbsp;·&nbsp; Generated: ${new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"})}</div>

          <div class="grid">
            <div>
              <h2>📦 Best Selling Products (Parent SKU)</h2>
              <table><thead><tr><th>SKU</th><th>Product</th><th>Units</th><th>Top Color</th><th>Top Size</th></tr></thead><tbody>
              ${skuList.map((s,i)=>{
                const tc=Object.entries(s.colors).sort((a,b)=>b[1]-a[1])[0]?.[0]||"—";
                const ts=Object.entries(s.sizes).sort((a,b)=>b[1]-a[1])[0]?.[0]||"—";
                return `<tr class="${i===0?"highlight":""}"><td>${s.sku}</td><td style="max-width:120px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${s.name}</td><td>${s.units}</td><td>${tc}</td><td>${ts}</td></tr>`;
              }).join("")}
              </tbody></table>

              <h2>📏 Best Selling Sizes</h2>
              <table><thead><tr><th>Size</th><th>Units</th><th>% of Sales</th><th>Meta Targeting</th></tr></thead><tbody>
              ${sizeList.map((s,i)=>`<tr class="${i===0?"highlight":""}"><td>${s.size}"</td><td>${s.units}</td><td>${Math.round(s.units/totalUnits*100)}%</td><td>${i===0?'<span class="badge">Primary target</span>':i<3?'<span class="badge" style="background:#2563eb">Secondary</span>':""}</td></tr>`).join("")}
              </tbody></table>

              <h2>🎨 Best Colors</h2>
              <table><thead><tr><th>Color</th><th>Units</th><th>% of Sales</th></tr></thead><tbody>
              ${colorList.map((s,i)=>`<tr class="${i===0?"highlight":""}"><td>${s.color}</td><td>${s.units}</td><td>${Math.round(s.units/totalUnits*100)}%</td></tr>`).join("")}
              </tbody></table>
            </div>

            <div>
              <h2>🗺 Best States (Geo Targeting)</h2>
              <table><thead><tr><th>State</th><th>Units</th><th>Revenue</th><th>%</th></tr></thead><tbody>
              ${stateList.map((s,i)=>`<tr class="${i<3?"highlight":""}"><td>${STATE_NAMES2[s.state]||s.state}</td><td>${s.units}</td><td>₹${Math.round(s.revenue).toLocaleString("en-IN")}</td><td>${Math.round(s.units/totalUnits*100)}%</td></tr>`).join("")}
              </tbody></table>

              <h2>🏙 Best Cities (Meta City Targeting)</h2>
              <table><thead><tr><th>City</th><th>State</th><th>Units</th><th>Revenue</th></tr></thead><tbody>
              ${cityList.map((c,i)=>`<tr class="${i<3?"highlight":""}"><td>${c.city}</td><td>${STATE_NAMES2[c.state]||c.state}</td><td>${c.units}</td><td>₹${Math.round(c.revenue).toLocaleString("en-IN")}</td></tr>`).join("")}
              </tbody></table>

              <h2>🎯 Meta Ads Recommendations</h2>
              <div class="tip">✅ <strong>Best product to promote:</strong> ${skuList[0]?.sku||"—"} (${skuList[0]?.name?.slice(0,40)||""})</div>
              <div class="tip" style="margin-top:4px">✅ <strong>Target sizes in creatives:</strong> ${sizeList.slice(0,3).map(s=>s.size+'"').join(", ")}</div>
              <div class="tip" style="margin-top:4px">✅ <strong>Top geo targets:</strong> ${stateList.slice(0,3).map(s=>STATE_NAMES2[s.state]||s.state).join(", ")}</div>
              <div class="tip" style="margin-top:4px">✅ <strong>Top cities for Meta Local:</strong> ${cityList.slice(0,4).map(c=>c.city).join(", ")}</div>
              <div class="tip" style="margin-top:4px">✅ <strong>Best color in ads:</strong> ${colorList[0]?.color||"—"} (${Math.round((colorList[0]?.units||0)/totalUnits*100)}% of sales)</div>
            </div>
          </div>
          </body></html>`;
          const w=window.open("","_blank","width=850,height=700");
          w.document.write(html);
          w.document.close();
          setTimeout(()=>w.print(),600);
          toast("Report sent to printer ✓","success");
        };

        // Color map for swatches
        const colorSwatches = { Black:"#1a1a1a", Brown:"#8B4513", Tan:"#d2a679", Navy:"#001f5b", Grey:"#6b7280", Blue:"#2563eb", "Silver":"#c0c0c0", White:"#f3f3f3", Olive:"#6b7a2a" };

        return (
          <div>
            {pOrders.length===0 ? (
              <div style={{ ...S.card, padding:48, textAlign:"center", color:"#9ca3af" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🎯</div>
                <div style={{ fontWeight:600, fontSize:14, marginBottom:6 }}>No order data for this period</div>
                <div style={{ fontSize:12, marginBottom:16 }}>Upload Myntra orders CSV and select a period to generate your Meta Ads report</div>
                <button style={S.btn("#b5003c")} onClick={()=>setTab("upload")}>📁 Upload Orders →</button>
              </div>
            ) : (
              <>
                {/* Header + export */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14, flexWrap:"wrap", gap:10 }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:15 }}>🎯 Meta Ads Intelligence Report</div>
                    <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>
                      Based on <strong>{totalUnits}</strong> Myntra orders{periodFilter!=="all"?" — "+periodFilter:" — All Time"} · Use these insights to target your Shopify Meta ads
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    <button style={S.btn("#1A3A5C")} onClick={printReport}>🖨️ Print Report</button>
                    <button style={{ ...S.btn("#1877F2") }} onClick={exportMetaAudienceCSV}>⬇ Geo CSV</button>
                    <button style={S.btnOut} onClick={exportProductInsightsCSV}>⬇ Product CSV</button>
                  </div>
                </div>

                {/* Meta recommendations banner */}
                <div style={{ background:"linear-gradient(135deg,#1877F2,#E1306C)", borderRadius:10, padding:"14px 18px", marginBottom:14, color:"#fff" }}>
                  <div style={{ fontWeight:700, fontSize:13, marginBottom:10 }}>🎯 Quick Meta Ads Recommendations</div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10 }}>
                    {[
                      { label:"Best SKU to promote",    value:skuList[0]?.sku||"—",    icon:"📦" },
                      { label:"Top sizes for creatives", value:sizeList.slice(0,2).map(s=>s.size+'"').join(", ")||"—", icon:"📏" },
                      { label:"Best color in creatives", value:colorList[0]?.color||"—", icon:"🎨" },
                      { label:"Top states to target",    value:stateList.slice(0,2).map(s=>STATE_NAMES2[s.state]||s.state).join(", ")||"—", icon:"🗺" },
                      { label:"Top cities",              value:cityList.slice(0,2).map(c=>c.city).join(", ")||"—", icon:"🏙" },
                    ].map(k=>(
                      <div key={k.label} style={{ background:"rgba(255,255,255,0.15)", borderRadius:8, padding:"8px 10px" }}>
                        <div style={{ fontSize:10, opacity:0.8, marginBottom:3 }}>{k.icon} {k.label}</div>
                        <div style={{ fontWeight:700, fontSize:12 }}>{k.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 4-panel grid */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                  {/* Best SKUs */}
                  <div style={S.card}>
                    <div style={S.hdr}><span>📦 Best Selling Products</span><span style={{ fontSize:10, color:"#9ca3af", fontWeight:400 }}>Parent SKU level</span></div>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead><tr>{["Rank","SKU","Units","Revenue","Top Color","Top Size"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {skuList.map((s,i)=>{
                          const tc = Object.entries(s.colors).sort((a,b)=>b[1]-a[1])[0]?.[0]||"—";
                          const ts = Object.entries(s.sizes).sort((a,b)=>b[1]-a[1])[0]?.[0]||"—";
                          return (
                            <tr key={s.sku} style={{ background:i===0?"#fff8f2":i%2?"#fafafa":"#fff" }}>
                              <td style={{ ...S.td, textAlign:"center" }}>
                                <span style={{ fontSize:14 }}>{i===0?"🥇":i===1?"🥈":i===2?"🥉":i+1}</span>
                              </td>
                              <td style={{ ...S.td, fontFamily:"monospace", fontWeight:700, color:"#F47920" }}>{s.sku}</td>
                              <td style={{ ...S.td, textAlign:"center", fontWeight:700, color:"#2563eb" }}>{s.units}</td>
                              <td style={{ ...S.td, textAlign:"right" }}>{INR2(s.revenue)}</td>
                              <td style={S.td}>
                                <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                                  {colorSwatches[tc] && <div style={{ width:10, height:10, borderRadius:"50%", background:colorSwatches[tc], border:"1px solid #ddd", flexShrink:0 }}/>}
                                  <span style={{ fontSize:11 }}>{tc}</span>
                                </div>
                              </td>
                              <td style={{ ...S.td, fontWeight:600, color:"#7e22ce" }}>{ts}"</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Best states */}
                  <div style={S.card}>
                    <div style={S.hdr}><span>🗺 Best States — Meta Geo Targeting</span></div>
                    <div style={{ padding:14 }}>
                      {stateList.map((s,i)=>{
                        const max = stateList[0].units;
                        const pct = Math.round(s.units/totalUnits*100);
                        return (
                          <div key={s.state} style={{ marginBottom:10 }}>
                            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:3 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                {i<3&&<span style={{ fontSize:14 }}>{i===0?"🥇":i===1?"🥈":"🥉"}</span>}
                                <span style={{ fontWeight:600 }}>{STATE_NAMES2[s.state]||s.state}</span>
                                {i<3&&<span style={{ fontSize:9, background:"#1877F2", color:"#fff", padding:"1px 6px", borderRadius:8, fontWeight:600 }}>Target</span>}
                              </div>
                              <span style={{ color:"#6b7280" }}>{s.units} orders · {pct}% · {INR2(s.revenue)}</span>
                            </div>
                            <div style={{ height:6, background:"#f3f4f6", borderRadius:3, overflow:"hidden" }}>
                              <div style={{ height:6, background:i===0?"#1877F2":i===1?"#E1306C":"#b5003c", width:(s.units/max*100)+"%", borderRadius:3 }}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12 }}>
                  {/* Sizes */}
                  <div style={S.card}>
                    <div style={S.hdr}><span>📏 Best Sizes</span></div>
                    <div style={{ padding:14 }}>
                      {sizeList.map((s,i)=>{
                        const pct = Math.round(s.units/totalUnits*100);
                        return (
                          <div key={s.size} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:"1px solid #f9fafb" }}>
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <div style={{ width:32, height:32, borderRadius:6, background:i===0?"#F47920":"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:i===0?"#fff":"#374151" }}>
                                {s.size}"
                              </div>
                              <div>
                                <div style={{ fontSize:11, fontWeight:600 }}>Size {s.size}</div>
                                <div style={{ fontSize:10, color:"#9ca3af" }}>{pct}% of sales</div>
                              </div>
                            </div>
                            <div style={{ textAlign:"right" }}>
                              <div style={{ fontWeight:700, color:"#2563eb" }}>{s.units} units</div>
                              {i===0&&<div style={{ fontSize:9, color:"#1877F2", fontWeight:600 }}>Use in ad creative</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Colors */}
                  <div style={S.card}>
                    <div style={S.hdr}><span>🎨 Best Colors</span></div>
                    <div style={{ padding:14 }}>
                      {colorList.length===0 ? <div style={{ color:"#9ca3af", fontSize:12 }}>No color data extracted from SKUs</div> :
                        colorList.map((s,i)=>{
                          const pct = Math.round(s.units/totalUnits*100);
                          const sw  = colorSwatches[s.color];
                          return (
                            <div key={s.color} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid #f9fafb" }}>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div style={{ width:28, height:28, borderRadius:"50%", background:sw||"#e5e7eb", border:"2px solid #e5e7eb", flexShrink:0 }}/>
                                <div>
                                  <div style={{ fontSize:11, fontWeight:600 }}>{s.color}</div>
                                  <div style={{ fontSize:10, color:"#9ca3af" }}>{pct}% of sales</div>
                                </div>
                              </div>
                              <div style={{ fontWeight:700, color:"#2563eb" }}>{s.units}</div>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>

                  {/* Cities */}
                  <div style={S.card}>
                    <div style={S.hdr}><span>🏙 Best Cities</span></div>
                    <div style={{ maxHeight:260, overflowY:"auto" }}>
                      {cityList.map((city,i)=>{
                        const pct = Math.round(city.units/totalUnits*100);
                        return (
                          <div key={city.city} style={{ padding:"8px 14px", borderBottom:"1px solid #f9fafb", display:"flex", justifyContent:"space-between", alignItems:"center", background:i%2?"#fafafa":"#fff" }}>
                            <div>
                              <div style={{ fontSize:12, fontWeight:i<3?700:400 }}>{city.city}</div>
                              <div style={{ fontSize:10, color:"#9ca3af" }}>{STATE_NAMES2[city.state]||city.state} · {pct}%</div>
                            </div>
                            <div style={{ textAlign:"right" }}>
                              <div style={{ fontWeight:600, color:"#2563eb" }}>{city.units}</div>
                              {i<3&&<div style={{ fontSize:9, background:"#1877F2", color:"#fff", padding:"1px 5px", borderRadius:6 }}>Target</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Size × State heatmap */}
                {stateList.length>0 && sizeList.length>0 && (
                  <div style={{ ...S.card, overflow:"hidden" }}>
                    <div style={S.hdr}><span>🔥 Size × State Heatmap — What sells where</span><span style={{ fontSize:10, color:"#9ca3af", fontWeight:400 }}>Use to target right size variants in right states</span></div>
                    <div style={{ overflowX:"auto" }}>
                      <table style={{ borderCollapse:"collapse", fontSize:11 }}>
                        <thead>
                          <tr>
                            <th style={{ ...S.th, minWidth:80 }}>Size</th>
                            {stateList.map(s=><th key={s.state} style={S.th}>{STATE_NAMES2[s.state]||s.state}</th>)}
                            <th style={S.th}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sizeList.map(sz=>(
                            <tr key={sz.size}>
                              <td style={{ ...S.td, fontWeight:700, color:"#7e22ce" }}>{sz.size}"</td>
                              {stateList.map(st=>{
                                const count = sizeStateMap[sz.size+"_"+st.state]||0;
                                const intensity = count>0 ? Math.min(1, count/3) : 0;
                                return (
                                  <td key={st.state} style={{ ...S.td, textAlign:"center", background:count>0?`rgba(24,119,242,${0.15+intensity*0.7})`:"#fafafa", fontWeight:count>0?700:400, color:count>0?"#fff":"#9ca3af" }}>
                                    {count||"—"}
                                  </td>
                                );
                              })}
                              <td style={{ ...S.td, textAlign:"center", fontWeight:700, color:"#2563eb" }}>{sz.units}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ padding:"10px 14px", background:"#eff6ff", fontSize:11, color:"#1e40af" }}>
                      💡 <strong>How to use:</strong> Darker blue = more orders for that size in that state. Use this to create state-specific Meta ad sets showing the right size variants.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      
      {/* ══ PAYMENT RECONCILIATION ══ */}
      {tab === "payment" && (() => {
        const INR2 = v => "₹"+(Math.round((Number(v)||0)*100)/100).toLocaleString("en-IN");
        const R2   = v => Math.round((Number(v)||0)*100)/100;

        // ── Marketing per order: Myntra PLA ads total spend ÷ total orders ──
        const _totalAdSpend   = (ads||[]).reduce((a,r)=>a+(r.spend||0), 0);
        const _totalOrders    = Math.max(settleRows.length, 1);  // ALL orders (delivered + returned)
        const marketingPerOrd = _totalAdSpend > 0
          ? Math.round(_totalAdSpend / _totalOrders * 100) / 100
          : 0;  // 0 if no ads report uploaded — add via Additional Costs

        // ── Build per-order P&L using settlement report as base ──
        const perOrderPL = settleRows.map(r => {
          // From settlement report — exact Myntra deductions
          const sp           = r.sellerProductAmt;       // what Myntra credits you
          const commission   = r.commission;             // total commission+TCS+TDS
          const logistics    = r.logistic;               // logistics charge
          const tcs          = r.tcs;                    // TCS @ 0.5%
          const tds          = r.tds;                    // TDS @ 0.1%
          const fixedCost    = r.fixedCost;              // fixed cost per order
          const netReceived  = r.finalSettled;           // actual bank transfer
          const pendingAmt   = r.payEligibleFw + r.payEligibleRv - r.finalSettled;

          // Commission breakdown
          const commBase     = R2(sp * r.commissionPct / 100);
          const commGST      = R2(commission - commBase - tcs - tds);
          const logisticsGST = R2(logistics * 18 / 100);

          // Is this a return? Define first — used in cost calculations below
          const isReturn     = !!r.returnStatus;

          // ── COGS: 0 for returned orders (product received back) ──────
          const cogEntry     = cogDefaults[r.sku] || {};
          const cog          = isReturn ? 0 : (cogEntry.cost || 0);

          // ── Marketing: from Ads & P&L tab (ad spend ÷ orders) ────────
          const marketing    = marketingPerOrd;  // applies even for returns (ad was spent)

          // ── Additional costs: ALL per-order costs from Additional Costs list ──
          // Includes packaging (₹18+₹1), any other per-order cost
          // For returns: 0 (product back, no packaging consumed)
          const additionalPerOrder = isReturn ? 0
            : (additionalCosts||[]).reduce((a, e) => {
                const amt = parseFloat(e.amount) || 0;
                if ((e.amountType || "per_order") === "per_order") return a + amt;
                // monthly fixed: divide by order count
                return a + (amt / Math.max(_deliveredCount, 1));
              }, 0);

          // ── GST on seller amount ──────────────────────────────────────
          // Output GST = what Myntra collects from customer on your behalf
          // Input ITC  = GST you paid on COGS (claimable)
          // Net GST    = amount you remit to government
          const outputGST  = R2(sp * 18 / 118);
          const taxableVal = R2(sp - outputGST);
          const inputGST   = R2(cog * 18 / 100);   // 0 for returns (cog=0)
          const netGST     = R2(outputGST - inputGST);
          // For returns: GST is reversed, net = 0
          const gstDeducted = isReturn ? 0 : netGST;

          // Net to you after ALL deductions
          const forwardEligible  = r.payEligibleFw || 0;
          const reverseEligible  = r.payEligibleRv || 0;
          const reverseLogistics = isReturn ? R2(Math.max(0, reverseEligible - sp)) : 0;
          const totalDeductions  = R2(commission + logistics + fixedCost);
          const netFromMyntra    = isReturn
            ? R2(forwardEligible - reverseEligible)
            : R2(sp - totalDeductions);
          // ── Profit = Net from Myntra − COGS − Marketing − Additional Costs − GST ──
          const profit = R2(netFromMyntra - cog - marketing - additionalPerOrder - gstDeducted);
          const margin = sp > 0 ? R2(profit / sp * 100) : (profit >= 0 ? 0 : -100);

          return {
            ...r, sp, commission, logistics, tcs, tds, fixedCost,
            commBase, commGST, logisticsGST,
            netReceived, pendingAmt,
            isReturn, cog, marketing, additionalPerOrder, gstDeducted,
            forwardEligible, reverseEligible, reverseLogistics,
            outputGST, inputGST, netGST, taxableVal,
            totalDeductions, netFromMyntra,
            profit, margin,
            isProfitable: profit >= 0,
          };
        });

        // ── Summary totals ──────────────────────────────────
        const totSP         = perOrderPL.reduce((a,r)=>a+r.sp,0);
        const totSellerAmt  = totSP; // alias used in bank recon section
        const totComm       = perOrderPL.reduce((a,r)=>a+r.commission,0);
        const totLogi       = perOrderPL.reduce((a,r)=>a+r.logistics,0);
        const totFixed      = perOrderPL.reduce((a,r)=>a+r.fixedCost,0);
        const totTCS        = perOrderPL.reduce((a,r)=>a+r.tcs,0);
        const totTDS        = perOrderPL.reduce((a,r)=>a+r.tds,0);
        const totDeductions = perOrderPL.reduce((a,r)=>a+r.totalDeductions,0);
        const totNet        = perOrderPL.reduce((a,r)=>a+r.netFromMyntra,0);
        const totReceived   = perOrderPL.reduce((a,r)=>a+r.netReceived,0);
        const totPending    = perOrderPL.reduce((a,r)=>a+r.pendingAmt,0);
        const totCOG           = perOrderPL.reduce((a,r)=>a+r.cog,0);
        const totNetGST        = perOrderPL.reduce((a,r)=>a+r.gstDeducted,0);
        const totMarketing     = perOrderPL.reduce((a,r)=>a+r.marketing,0);
        const totAdditional    = perOrderPL.reduce((a,r)=>a+r.additionalPerOrder,0);
        const totProfit        = perOrderPL.reduce((a,r)=>a+r.profit,0);
        const returnOrders     = perOrderPL.filter(r=>r.isReturn);
        const profitableOrders  = perOrderPL.filter(r=>r.isProfitable);
        const lossOrders        = perOrderPL.filter(r=>!r.isProfitable);
        const settled_orders    = perOrderPL.filter(r=>r.fwdStatus==="SETTLED");
        const unsettled_orders  = perOrderPL.filter(r=>r.fwdStatus!=="SETTLED");
        const avgMargin         = totSP>0 ? R2(totProfit/totSP*100) : 0;

        // UTR groupings
        const utrMap = {};
        settled_orders.forEach(r=>{
          if (!utrMap[r.utr]) utrMap[r.utr] = { utr:r.utr, date:r.settleDate, orders:0, amount:0 };
          utrMap[r.utr].orders++;
          utrMap[r.utr].amount += r.netReceived;
        });
        const utrList = Object.values(utrMap).sort((a,b)=>new Date(b.date)-new Date(a.date));

        // Export full P&L
        const exportPL = () => {
          const hdr = ["Order ID","SKU","Style","Order Date","Delivered","Zone","Return?","Status","UTR",
            "MRP","Customer Paid","Seller Product Amt",
            "Commission %","Commission","Commission GST","TCS","TDS","Logistics","Logistics GST","Fixed Cost","Total Deductions",
            "Net from Myntra","Actual Received","Pending",
            "Output GST","Input GST (ITC)","Net GST to Govt",
            "COGS (0 if returned)","Packaging","Marketing/Ads","Other Costs","Total Own Costs",
            "Net Profit","Margin %","Result"];
          const rows2 = perOrderPL.map(r=>[
            r.orderId, r.sku, r.styleName, r.orderDate, r.deliveredOn, r.zone,
            r.isReturn?"YES":"NO", r.fwdStatus, r.utr,
            r.mrp, r.customerPaid, r.sp,
            r.commissionPct+"%", r.commBase.toFixed(2), r.commGST.toFixed(2),
            r.tcs.toFixed(2), r.tds.toFixed(2),
            r.logistics.toFixed(2), r.logisticsGST.toFixed(2),
            r.fixedCost.toFixed(2), r.totalDeductions.toFixed(2),
            r.netFromMyntra.toFixed(2), r.netReceived.toFixed(2), r.pendingAmt.toFixed(2),
            r.outputGST.toFixed(2), r.inputGST.toFixed(2), r.netGST.toFixed(2),
            r.cog.toFixed(2), r.packaging.toFixed(2), r.marketing.toFixed(2), r.extraPerOrder.toFixed(2),
            (r.cog+r.packaging+r.marketing+r.extraPerOrder).toFixed(2),
            r.profit.toFixed(2), r.margin.toFixed(1)+"%", r.isProfitable?"PROFIT":"LOSS"
          ]);
          const csv = [hdr,...rows2].map(row=>row.map(v=>`"${v}"`).join(",")).join("\n");
          const a=document.createElement("a"); a.href="data:text/csv,"+encodeURIComponent(csv);
          a.download="Myntra_PerOrder_PnL.csv"; a.click();
          toast("Per-order P&L exported ✓","success");
        };

        if (settleRows.length === 0 && payRows.length === 0) return (
          <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:48, textAlign:"center", color:"#9ca3af" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>💳</div>
            <div style={{ fontWeight:600, fontSize:14, marginBottom:6 }}>Upload Settlement Report first</div>
            <div style={{ fontSize:12, marginBottom:16 }}>Go to Upload tab → "Payment / Settlement Reports" → upload the 20260608_*.csv file</div>
            <button style={{ padding:"8px 18px", borderRadius:7, background:"#b5003c", color:"#fff", border:"none", cursor:"pointer", fontFamily:"inherit", fontWeight:600 }} onClick={()=>setTab("upload")}>📁 Upload →</button>
          </div>
        );

        return (
          <div>
            {/* Header */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:16 }}>💳 Per-Order P&L — Myntra</div>
                <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>
                  Exact deductions from Myntra settlement · {perOrderPL.length} orders · {profitableOrders.length} profitable · {lossOrders.length} loss
                  {perOrderPL.length>0 && !cogDefaults[perOrderPL[0]?.sku] && <span style={{ color:"#d97706" }}> · ⚠ Add COGS in Ads tab for complete P&L</span>}
                </div>
              </div>
              <button style={{ padding:"7px 16px", borderRadius:7, background:"#15803d", color:"#fff", border:"none", cursor:"pointer", fontFamily:"inherit", fontWeight:600, fontSize:12 }} onClick={exportPL}>⬇ Export Full P&L</button>
            </div>

            {/* ══ CM1 / CM2 / CM3 PROFIT WATERFALL ══ */}
            {(() => {
              // Net Settlement = what Myntra pays you, minus GST you remit to govt
              const netSettlement = R2(totNet - totNetGST);
              const cm1 = R2(netSettlement - totCOG);          // after product cost
              const cm2 = R2(cm1 - totAdditional);             // after packaging / additional
              const cm3 = R2(cm2 - totMarketing);              // after marketing = final profit
              const cm3Pct = totSP > 0 ? R2(cm3 / totSP * 100) : 0;
              const cmCard = (label, value, sub, color, big) => (
                <div key={label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"13px 15px", borderLeft:"3px solid "+color }}>
                  <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", marginBottom:3 }}>{label}</div>
                  <div style={{ fontSize:big?23:19, fontWeight:700, color:color }}>{INR2(value)}</div>
                  <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{sub}</div>
                </div>
              );
              return (
                <div style={{ marginBottom:14 }}>
                  {/* Settlement layer */}
                  <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"1px", margin:"2px 0 8px 2px" }}>📥 What Myntra pays you</div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
                    {cmCard("Seller Product Amt", totSP, "What Myntra credits you", "#2563eb")}
                    {cmCard("− Total Deductions", totDeductions, "Commission + Logistics + Fixed", "#dc2626")}
                    {cmCard("Net from Myntra", totNet, "Before your own costs", "#F47920")}
                    {cmCard("− GST to Govt", totNetGST, "Output GST − Input ITC", "#6366f1")}
                  </div>

                  {/* CM waterfall */}
                  <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"1px", margin:"2px 0 8px 2px" }}>💰 Your real profit — CM1 · CM2 · CM3</div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:10 }}>
                    <div style={{ background:"#1A3A5C", color:"#fff", borderRadius:10, padding:"13px 15px" }}>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,0.6)", fontWeight:700, textTransform:"uppercase", marginBottom:3 }}>Net Settlement</div>
                      <div style={{ fontSize:19, fontWeight:700 }}>{INR2(netSettlement)}</div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.5)", marginTop:2 }}>Your money (after GST)</div>
                    </div>
                    {cmCard("CM1 — after COGS", cm1, `− ${INR2(totCOG)} product cost`, cm1>=0?"#15803d":"#dc2626")}
                    {cmCard("CM2 — after packaging", cm2, `− ${INR2(totAdditional)} packaging/extra`, cm2>=0?"#15803d":"#dc2626")}
                    {cmCard("CM3 — after marketing", cm3, `− ${INR2(totMarketing)} ad spend`, cm3>=0?"#15803d":"#dc2626")}
                  </div>

                  {/* Final verdict */}
                  <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:10 }}>
                    <div style={{ background: cm3>=0?"#0f2818":"#2a0f0f", borderRadius:10, padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <div style={{ fontSize:11, color:"rgba(255,255,255,0.6)", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px" }}>CM3 — Final Profit</div>
                        <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", marginTop:2 }}>{cm3Pct}% of seller amount</div>
                      </div>
                      <div style={{ fontSize:30, fontWeight:800, color: cm3>=0?"#4ade80":"#f87171" }}>{INR2(cm3)}</div>
                    </div>
                    {cmCard("Actual Received", totReceived, `${INR(totPending)} pending`, "#15803d")}
                    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"13px 15px", borderLeft:"3px solid #7e22ce" }}>
                      <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", marginBottom:3 }}>Profit / Loss Split</div>
                      <div style={{ fontSize:19, fontWeight:700, color:"#7e22ce" }}>{profitableOrders.length}P / {lossOrders.length}L</div>
                      <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{Math.round(profitableOrders.length/Math.max(perOrderPL.length,1)*100)}% profitable</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ══ BANK RECONCILIATION ══ */}
            <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden", marginBottom:14 }}>
              <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span>🏦 Bank Reconciliation — What you received vs what should have been paid</span>
                <span style={{ fontSize:11, color:"#9ca3af", fontWeight:400 }}>Settlement CSV × PnL Excel</span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:0 }}>

                {/* Column 1: Myntra's calculation */}
                <div style={{ padding:16, borderRight:"1px solid #f3f4f6" }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#1A3A5C", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.4px" }}>📊 As per Myntra Settlement</div>
                  {[
                    { label:"Seller Product Amount",         val: totSellerAmt,                                       color:"#15803d", bold:false },
                    { label:"− Commission + TCS + TDS",      val:-(totComm),                                          color:"#dc2626", bold:false },
                    { label:"− Logistics",                   val:-(totLogi),                                          color:"#d97706", bold:false },
                    { label:"− Fixed Cost",                  val:-(totFixed),                                         color:"#d97706", bold:false },
                    { label:"Forward Eligible (total)",      val: settled_orders.reduce((a,r)=>a+r.forwardEligible,0)+unsettled_orders.reduce((a,r)=>a+r.forwardEligible,0), color:"#2563eb", bold:true },
                    { label:"− Reverse clawback (returns)",  val:-settleRows.filter(r=>r.returnStatus).reduce((a,r)=>a+(r.payEligibleRv||0),0), color:"#dc2626", bold:false },
                    { label:"Net payable to you",            val: totNet - settleRows.filter(r=>r.returnStatus).reduce((a,r)=>a+(r.payEligibleRv||0),0), color:"#15803d", bold:true },
                  ].map((row,i)=>(
                    <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:row.bold?"2px solid #e5e7eb":"1px solid #f9fafb", marginBottom:row.bold?4:0 }}>
                      <span style={{ fontSize:11, color:row.bold?"#1A3A5C":"#6b7280", fontWeight:row.bold?700:400 }}>{row.label}</span>
                      <span style={{ fontSize:12, fontWeight:row.bold?700:500, color:row.color }}>{row.val<0?"−":""}{INR2(Math.abs(row.val))}</span>
                    </div>
                  ))}
                </div>

                {/* Column 2: Bank transfer status */}
                <div style={{ padding:16, borderRight:"1px solid #f3f4f6" }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#1A3A5C", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.4px" }}>🏦 Bank Transfer Status</div>

                  {/* UTR transfers */}
                  {utrList.map((u,i)=>(
                    <div key={u.utr} style={{ marginBottom:10, padding:"8px 10px", background:"#f0fdf4", borderRadius:8, border:"1px solid #bbf7d0" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                        <span style={{ fontFamily:"monospace", fontSize:10, color:"#2563eb", fontWeight:600 }}>{u.utr.slice(-14)}</span>
                        <span style={{ fontWeight:700, fontSize:13, color:"#15803d" }}>{INR2(u.amount)}</span>
                      </div>
                      <div style={{ fontSize:10, color:"#9ca3af" }}>{u.orders} orders · {u.date?.slice(0,10)}</div>
                    </div>
                  ))}

                  <div style={{ borderTop:"2px solid #e5e7eb", marginTop:4, paddingTop:8 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, fontWeight:700, color:"#15803d", marginBottom:6 }}>
                      <span>✅ Total Received</span><span>{INR2(totReceived)}</span>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, fontWeight:600, color:"#d97706", marginBottom:4 }}>
                      <span>⏳ Pending (fwd)</span><span>{INR2(unsettled_orders.reduce((a,r)=>a+(r.forwardEligible||0),0))}</span>
                    </div>
                    {settleRows.filter(r=>r.returnStatus&&r.revStatus!=="SETTLED").length>0&&(
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, fontWeight:600, color:"#dc2626", marginBottom:4 }}>
                        <span>↩ Return clawback pending</span>
                        <span>−{INR2(settleRows.filter(r=>r.returnStatus&&r.revStatus!=="SETTLED").reduce((a,r)=>a+(r.payEligibleRv||0),0))}</span>
                      </div>
                    )}
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, fontWeight:700, color:"#2563eb", borderTop:"1px solid #e5e7eb", paddingTop:6, marginTop:4 }}>
                      <span>Net still to receive</span>
                      <span>{INR2(
                        unsettled_orders.reduce((a,r)=>a+(r.forwardEligible||0),0) -
                        settleRows.filter(r=>r.returnStatus&&r.revStatus!=="SETTLED").reduce((a,r)=>a+(r.payEligibleRv||0),0)
                      )}</span>
                    </div>
                  </div>
                </div>

                {/* Column 3: What should have been paid vs what was */}
                <div style={{ padding:16 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#1A3A5C", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.4px" }}>✅ Verification</div>
                  {(() => {
                    const shouldHavePaid   = settled_orders.reduce((a,r)=>a+(r.forwardEligible||0),0);
                    const actuallyPaid     = totReceived;
                    const diff             = R2(actuallyPaid - shouldHavePaid);
                    const pendingFwd       = unsettled_orders.reduce((a,r)=>a+(r.forwardEligible||0),0);
                    const pendingRevClawbk = settleRows.filter(r=>r.returnStatus&&r.revStatus!=="SETTLED").reduce((a,r)=>a+(r.payEligibleRv||0),0);
                    const netPending       = R2(pendingFwd - pendingRevClawbk);
                    return (
                      <div>
                        <div style={{ background:Math.abs(diff)<2?"#f0fdf4":"#fef2f2", borderRadius:8, padding:"10px 12px", marginBottom:10, border:`1px solid ${Math.abs(diff)<2?"#bbf7d0":"#fecaca"}` }}>
                          <div style={{ fontSize:10, color:"#6b7280", marginBottom:4 }}>Forward eligible (settled orders)</div>
                          <div style={{ fontSize:13, fontWeight:700, color:"#2563eb" }}>{INR2(shouldHavePaid)}</div>
                          <div style={{ fontSize:10, color:"#6b7280", marginTop:6, marginBottom:4 }}>Actually received in bank</div>
                          <div style={{ fontSize:13, fontWeight:700, color:"#15803d" }}>{INR2(actuallyPaid)}</div>
                          <div style={{ marginTop:8, fontSize:12, fontWeight:700, color:Math.abs(diff)<2?"#15803d":"#dc2626" }}>
                            {Math.abs(diff)<2 ? "✅ Match — amounts correct" : `❌ Difference: ${INR2(diff)}`}
                          </div>
                        </div>
                        {[
                          { label:"Settled orders",        val:settled_orders.length,   unit:"orders" },
                          { label:"Unsettled pending",     val:unsettled_orders.length, unit:"orders" },
                          { label:"Return clawback pending",val:settleRows.filter(r=>r.returnStatus&&r.revStatus!=="SETTLED").length, unit:"returns" },
                        ].map(r=>(
                          <div key={r.label} style={{ display:"flex", justifyContent:"space-between", fontSize:11, padding:"5px 0", borderBottom:"1px solid #f9fafb" }}>
                            <span style={{ color:"#6b7280" }}>{r.label}</span>
                            <span style={{ fontWeight:600 }}>{r.val} {r.unit}</span>
                          </div>
                        ))}
                        <div style={{ marginTop:10, background:"#eff6ff", borderRadius:7, padding:"8px 10px", fontSize:11, color:"#1e40af" }}>
                          💰 You should receive <strong>{INR2(netPending)}</strong> more after all pending forward payments arrive and return clawbacks are processed.
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:14, marginBottom:14 }}>
              {/* Deduction breakdown */}
              <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
                <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>📊 Where did the money go? (Total across all orders)</div>
                <div style={{ padding:16 }}>
                  {[
                    { label:"Customer Paid (MRP − discount)",       amt:perOrderPL.reduce((a,r)=>a+r.customerPaid,0),                              color:"#2563eb", indent:0  },
                    { label:"↳ Myntra keeps (discount funded)",      amt:perOrderPL.reduce((a,r)=>a+r.customerPaid-r.sp,0),                        color:"#9ca3af", indent:1  },
                    { label:"Your Seller Product Amount",            amt:totSP,                                                                      color:"#15803d", indent:0, bold:true },
                    { label:"  − Commission @17.7%",                 amt:-perOrderPL.reduce((a,r)=>a+r.commBase,0),                                 color:"#dc2626", indent:1  },
                    { label:"  − GST on Commission",                 amt:-perOrderPL.reduce((a,r)=>a+r.commGST,0),                                  color:"#dc2626", indent:1  },
                    { label:"  − TCS @0.5%",                         amt:-totTCS,                                                                    color:"#6366f1", indent:1  },
                    { label:"  − TDS @0.1%",                         amt:-totTDS,                                                                    color:"#6366f1", indent:1  },
                    { label:"  − Logistics",                         amt:-perOrderPL.reduce((a,r)=>a+r.logistics,0),                                color:"#d97706", indent:1  },
                    { label:"  − Fixed Cost (packaging/label)",      amt:-totFixed,                                                                  color:"#d97706", indent:1  },
                    { label:"Net from Myntra (= bank transfer)",     amt:totNet,                                                                     color:"#F47920", indent:0, bold:true },
                    { label:`  − COGS (${perOrderPL.filter(r=>r.cog>0).length} delivered, ${returnOrders.length} returns = ₹0)`, amt:-totCOG, color:"#d97706", indent:1 },
                    { label:`  − Marketing / Ads (${INR2(_totalAdSpend)} PLA spend ÷ ${_totalOrders} orders = ${INR2(marketingPerOrd)}/order)`, amt:-totMarketing, color:"#b5003c", indent:1 },
                    { label:`  − Additional Costs (${INR2(totAdditional/Math.max(perOrderPL.filter(r=>!r.isReturn).length,1))}/order, returns excluded)`, amt:-totAdditional, color:"#F47920", indent:1 },
                    { label:`  − GST on Seller Amt (output − ITC, returns excluded)`,   amt:-totNetGST,     color:"#6366f1", indent:1 },
                    { label:"NET PROFIT / LOSS",                     amt:totProfit,                                                                  color:totProfit>=0?"#15803d":"#dc2626", indent:0, bold:true, big:true },
                  ].map((row,i)=>(
                    <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:row.big?"10px 0 0":"4px 0", paddingLeft:(row.indent||0)*16, borderTop:row.big?"2px solid #e5e7eb":row.bold?"1px solid #e5e7eb":"none", marginTop:row.bold&&!row.big?4:0 }}>
                      <span style={{ fontSize:row.big?13:11, fontWeight:row.bold?700:400, color:row.bold?"#1A3A5C":"#6b7280" }}>{row.label}</span>
                      <span style={{ fontSize:row.big?16:12, fontWeight:row.bold?700:500, color:row.color }}>{row.amt<0?"−":""}{INR2(Math.abs(row.amt))}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bank settlement tracker */}
              <div>
                <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden", marginBottom:12 }}>
                  <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>🏦 Bank Transfers (UTR)</div>
                  {utrList.length===0 ? (
                    <div style={{ padding:20, textAlign:"center", color:"#9ca3af", fontSize:12 }}>No settled payments yet</div>
                  ) : utrList.map((u,i)=>(
                    <div key={u.utr} style={{ padding:"10px 14px", borderBottom:"1px solid #f3f4f6" }}>
                      <div style={{ fontFamily:"monospace", fontSize:10, color:"#2563eb", fontWeight:600, marginBottom:2 }}>{u.utr.slice(-12)}</div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <div style={{ fontSize:11, color:"#9ca3af" }}>{u.orders} orders · {u.date?.slice(0,10)}</div>
                        <div style={{ fontWeight:700, fontSize:14, color:"#15803d" }}>{INR2(u.amount)}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{ padding:"8px 14px", background:"#f0fdf4", borderTop:"1px solid #bbf7d0", display:"flex", justifyContent:"space-between", fontSize:12, fontWeight:700, color:"#15803d" }}>
                    <span>Total Received</span><span>{INR2(totReceived)}</span>
                  </div>
                  {totPending > 0 && (
                    <div style={{ padding:"8px 14px", background:"#fffbeb", borderTop:"1px solid #fde68a", display:"flex", justifyContent:"space-between", fontSize:12, fontWeight:700, color:"#d97706" }}>
                      <span>⏳ Pending</span><span>{INR2(totPending)}</span>
                    </div>
                  )}
                </div>

                {/* Return impact */}
                {settleRows.filter(r=>r.returnStatus).length>0 && (
                  <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
                    <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, color:"#dc2626" }}>↩️ Return Impact</div>
                    <div style={{ padding:14 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
                        <span style={{ color:"#6b7280" }}>Orders with returns</span>
                        <span style={{ fontWeight:600 }}>{settleRows.filter(r=>r.returnStatus).length}</span>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
                        <span style={{ color:"#6b7280" }}>Reverse eligible</span>
                        <span style={{ fontWeight:600, color:"#F47920" }}>{INR2(settleRows.filter(r=>r.returnStatus).reduce((a,r)=>a+r.payEligibleRv,0))}</span>
                      </div>
                      <div style={{ fontSize:10, color:"#9ca3af", marginTop:6 }}>Returns affect reverse payment settlement separately from forward settlement</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Per-order table */}
            <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
              <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span>📋 Per-Order Breakdown — All Deductions & P&L</span>
                <span style={{ fontSize:11, color:"#9ca3af", fontWeight:400 }}>Green = profit · Red = loss · Orange = unsettled</span>
              </div>
              <div style={{ overflowX:"auto", maxHeight:520, overflowY:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
                  <thead style={{ position:"sticky", top:0 }}>
                    <tr style={{ background:"#f9fafb" }}>
                      <th style={{ padding:"7px 10px", textAlign:"left",  fontSize:10, fontWeight:700, color:"#9ca3af", whiteSpace:"nowrap" }}>Order / SKU</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#9ca3af", whiteSpace:"nowrap" }}>Customer Paid</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#9ca3af", whiteSpace:"nowrap" }}>Seller Amt</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#dc2626",  whiteSpace:"nowrap" }}>Commission</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#d97706",  whiteSpace:"nowrap" }}>Logistics</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#6366f1",  whiteSpace:"nowrap" }}>TCS+TDS</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#9ca3af",  whiteSpace:"nowrap" }}>Fixed</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#F47920",  whiteSpace:"nowrap" }}>Net (Myntra)</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#d97706",  whiteSpace:"nowrap" }}>COGS</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#b5003c",  whiteSpace:"nowrap" }}>Marketing</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#F47920",  whiteSpace:"nowrap" }}>Addl Costs</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#6366f1",  whiteSpace:"nowrap" }}>GST</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#15803d",  whiteSpace:"nowrap" }}>Received</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#7e22ce",  whiteSpace:"nowrap" }}>Profit</th>
                      <th style={{ padding:"7px 10px", textAlign:"right", fontSize:10, fontWeight:700, color:"#7e22ce",  whiteSpace:"nowrap" }}>Margin</th>
                      <th style={{ padding:"7px 10px", textAlign:"left",  fontSize:10, fontWeight:700, color:"#9ca3af",  whiteSpace:"nowrap" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perOrderPL.map((r,i) => {
                      const bg = r.returnStatus ? "#fff5f5" : !r.isProfitable ? "#fef2f2" : r.fwdStatus==="SETTLED" ? "#f0fdf4" : i%2?"#fafafa":"#fff";
                      const pc = r.isProfitable ? "#15803d" : "#dc2626";
                      return (
                        <tr key={r.orderId} style={{ background:bg }}>
                          <td style={{ padding:"8px 10px", minWidth:140 }}>
                            <div style={{ fontFamily:"monospace", fontSize:10, color:"#b5003c", fontWeight:600 }}>{r.orderId}</div>
                            <div style={{ fontSize:10, color:"#F47920", fontFamily:"monospace" }}>{r.sku}</div>
                            <div style={{ fontSize:9, color:"#9ca3af" }}>{r.zone} · {r.state} · {r.orderDate?.slice(0,10)}</div>
                            {r.returnStatus && <div style={{ fontSize:9, color:"#dc2626", fontWeight:600 }}>↩ {r.returnStatus}</div>}
                          </td>
                          <td style={{ padding:"8px 10px", textAlign:"right", color:"#6b7280" }}>{INR2(r.customerPaid)}</td>
                          <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:600 }}>{INR2(r.sp)}</td>
                          <td style={{ padding:"8px 10px", textAlign:"right", color:"#dc2626" }}>
                            {INR2(r.commBase)}
                            <div style={{ fontSize:9, color:"#9ca3af" }}>{r.commissionPct}%</div>
                          </td>
                          <td style={{ padding:"8px 10px", textAlign:"right", color:"#d97706" }}>
                            {INR2(r.logistics)}
                            {r.isReturn && r.reverseLogistics>0 && <div style={{ fontSize:9, color:"#dc2626" }}>+{INR2(r.reverseLogistics)} rev.logi</div>}
                          </td>
                          <td style={{ padding:"8px 10px", textAlign:"right", color:"#6366f1" }}>
                            {INR2(r.tcs+r.tds)}
                            <div style={{ fontSize:9, color:"#9ca3af" }}>T:{INR2(r.tcs)} D:{INR2(r.tds)}</div>
                          </td>
                          <td style={{ padding:"8px 10px", textAlign:"right", color:"#9ca3af" }}>{INR2(r.fixedCost)}</td>
                          <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:600, color:r.isReturn?"#dc2626":"#F47920" }}>
                            {r.isReturn
                              ? <span title={`Fwd: ${INR2(r.forwardEligible)} − Rev: ${INR2(r.reverseEligible)}`}>{INR2(r.netFromMyntra)}</span>
                              : INR2(r.netFromMyntra)}
                            {r.isReturn && <div style={{ fontSize:9, color:"#9ca3af" }}>fwd{INR2(r.forwardEligible)}−rv{INR2(r.reverseEligible)}</div>}
                          </td>
                          <td style={{ padding:"8px 10px", textAlign:"right", color:"#d97706" }}>
                            {r.isReturn
                              ? <span style={{ color:"#9ca3af", fontSize:10 }}>—&nbsp;↩</span>
                              : r.cog>0 ? INR2(r.cog) : <span style={{ color:"#9ca3af" }}>—</span>}
                          </td>
                          <td style={{ padding:"8px 10px", textAlign:"right", color:"#b5003c" }}>{INR2(r.marketing)}</td>
                          <td style={{ padding:"8px 10px", textAlign:"right", color:"#F47920" }}>
                            {r.isReturn
                              ? <span style={{ color:"#9ca3af", fontSize:10 }}>—&nbsp;↩</span>
                              : INR2(r.additionalPerOrder)}
                          </td>
                          <td style={{ padding:"8px 10px", textAlign:"right", color:"#6366f1" }}>
                            {r.isReturn
                              ? <span style={{ color:"#9ca3af", fontSize:10 }}>—&nbsp;↩</span>
                              : INR2(r.gstDeducted)}
                          </td>
                          <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:600, color:r.netFromMyntra>=0?"#15803d":"#dc2626" }}>
                            {r.isReturn
                              ? <span style={{ color:r.netCashReturn>=0?"#15803d":"#dc2626", fontWeight:700 }}>{INR2(r.netCashReturn)}</span>
                              : r.netReceived>0 ? INR2(r.netReceived) : <span style={{ color:"#d97706", fontSize:10 }}>⏳ {INR2(r.pendingAmt)}</span>}
                          </td>
                          <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:700, fontSize:13, color:pc }}>
                            {r.isProfitable?"▲":"▼"}{INR2(Math.abs(r.profit))}
                          </td>
                          <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:600, color:pc }}>
                            {r.margin.toFixed(1)}%
                          </td>
                          <td style={{ padding:"8px 10px" }}>
                            <span style={{ fontSize:9, padding:"2px 7px", borderRadius:10, fontWeight:700,
                              background:r.fwdStatus==="SETTLED"?"#f0fdf4":"#fffbeb",
                              color:r.fwdStatus==="SETTLED"?"#15803d":"#d97706" }}>
                              {r.fwdStatus==="SETTLED"?"✅ Settled":"⏳ Pending"}
                            </span>
                            {r.returnStatus && <div style={{ fontSize:9, color:"#dc2626", marginTop:2 }}>↩ Return</div>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background:"#f9fafb", fontWeight:700, position:"sticky", bottom:0, borderTop:"2px solid #e5e7eb" }}>
                      <td style={{ padding:"8px 10px", fontWeight:700, fontSize:12 }}>TOTAL ({perOrderPL.length} orders)</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", color:"#6b7280" }}>{INR2(perOrderPL.reduce((a,r)=>a+r.customerPaid,0))}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:700 }}>{INR2(totSP)}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", color:"#dc2626", fontWeight:700 }}>{INR2(totComm)}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", color:"#d97706", fontWeight:700 }}>{INR2(totLogi)}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", color:"#6366f1", fontWeight:700 }}>{INR2(totTCS+totTDS)}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", color:"#9ca3af", fontWeight:700 }}>{INR2(totFixed)}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", color:"#F47920", fontWeight:700 }}>{INR2(totNet)}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", color:"#d97706", fontWeight:700 }}>{INR2(totCOG)}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", color:"#b5003c", fontWeight:700 }}>{INR2(totMarketing)}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", color:"#F47920", fontWeight:700 }}>{INR2(totAdditional)}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", color:"#6366f1", fontWeight:700 }}>{INR2(totNetGST)}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", color:"#15803d", fontWeight:700 }}>{INR2(totReceived)}</td>
                      <td style={{ padding:"8px 10px", textAlign:"right", fontSize:14, fontWeight:700, color:totProfit>=0?"#15803d":"#dc2626" }}>
                        {totProfit>=0?"▲":"▼"}{INR2(Math.abs(totProfit))}
                      </td>
                      <td style={{ padding:"8px 10px", textAlign:"right", fontWeight:700, color:totProfit>=0?"#15803d":"#dc2626" }}>{avgMargin.toFixed(1)}%</td>
                      <td style={{ padding:"8px 10px" }}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

            {/* ══ RECONCILIATION ══ */}
      {tab === "reconcile" && (
        <div>
          <div style={{ background:"#fff0f5", borderRadius:8, padding:"10px 14px", marginBottom:12, fontSize:12, color:"#b5003c" }}>
            💡 Match your delivered orders against returns to calculate net payout. Cross-check against Myntra's payment statement.
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
            {[
              { label:"Delivered Orders",      value:delivered.length,                   color:"#15803d" },
              { label:"Returned from Delivered",value:reconRows.filter(o=>o.hasReturn).length, color:"#dc2626" },
              { label:"Clean Deliveries",       value:reconRows.filter(o=>!o.hasReturn).length, color:"#2563eb" },
              { label:"Net Payout",             value:INR(reconRows.reduce((a,o)=>a+o.netAmt,0)), color:"#F47920" },
            ].map(k=>(
              <div key={k.label} style={{ ...S.kpi(k.color) }}>
                <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                <div style={{ fontSize:18, fontWeight:700, color:k.color, marginTop:2 }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={S.hdr}>
              <span>🔄 Order-level Reconciliation</span>
              <button style={S.btnOut} onClick={()=>exportCSV(reconRows.map(o=>({orderId:o.orderId,sku:o.sku,productName:o.productName,sellerPrice:o.sellerPrice,gtCharges:o.gtCharges,hasReturn:o.hasReturn?"YES":"NO",returnReason:o.ret?.returnReason||"",netAmt:o.netAmt})),"myntra_reconciliation")}>⬇ Export</button>
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>{["Order ID","SKU","Product","Seller Price","GT Charges","Return?","Return Reason","Net Payout"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {reconRows.length===0 ? (
                    <tr><td colSpan={8} style={{ ...S.td, textAlign:"center", padding:40, color:"#9ca3af" }}>Upload orders CSV to reconcile</td></tr>
                  ) : reconRows.map((o,i)=>(
                    <tr key={o.orderId+i} style={{ background:o.hasReturn?"#fff5f5":i%2?"#fafafa":"#fff" }}>
                      <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#b5003c" }}>{o.orderId}</td>
                      <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{o.sku}</td>
                      <td style={{ ...S.td, maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{o.productName}</td>
                      <td style={{ ...S.td, textAlign:"right", color:"#15803d", fontWeight:600 }}>{INR(o.sellerPrice)}</td>
                      <td style={{ ...S.td, textAlign:"right", color:"#dc2626" }}>{INR(o.gtCharges)}</td>
                      <td style={S.td}>
                        <span style={{ fontSize:9, padding:"2px 7px", borderRadius:10, background:o.hasReturn?"#fef2f2":"#f0fdf4", color:o.hasReturn?"#dc2626":"#15803d", fontWeight:700 }}>
                          {o.hasReturn?"↩ Returned":"✅ Clean"}
                        </span>
                      </td>
                      <td style={{ ...S.td, fontSize:10, color:"#6b7280" }}>{o.ret?.returnReason||"—"}</td>
                      <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:o.netAmt>0?"#15803d":"#dc2626" }}>{INR(o.netAmt)}</td>
                    </tr>
                  ))}
                  {reconRows.length>0 && (
                    <tr style={{ background:"#f9fafb", fontWeight:700 }}>
                      <td colSpan={3} style={{ ...S.td, fontWeight:700 }}>TOTAL ({reconRows.length} orders)</td>
                      <td style={{ ...S.td, textAlign:"right", color:"#15803d", fontWeight:700 }}>{INR(reconRows.reduce((a,o)=>a+o.sellerPrice,0))}</td>
                      <td style={{ ...S.td, textAlign:"right", color:"#dc2626", fontWeight:700 }}>{INR(reconRows.reduce((a,o)=>a+o.gtCharges,0))}</td>
                      <td colSpan={2} style={S.td}></td>
                      <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:"#F47920" }}>{INR(reconRows.reduce((a,o)=>a+o.netAmt,0))}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══ ANALYTICS ══ */}
      {tab === "analytics" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            {/* SKU performance */}
            <div style={S.card}>
              <div style={S.hdr}><span>📦 SKU Performance</span></div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>{["SKU","Product","Units","Revenue","Seller Earn"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {skuList.length===0 ? (
                      <tr><td colSpan={5} style={{ ...S.td, textAlign:"center", padding:24, color:"#9ca3af" }}>No data</td></tr>
                    ) : skuList.map((s,i)=>(
                      <tr key={s.sku} style={{ background:i%2?"#fafafa":"#fff" }}>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{s.sku}</td>
                        <td style={{ ...S.td, maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontSize:11 }}>{s.name}</td>
                        <td style={{ ...S.td, textAlign:"center", fontWeight:700, color:"#2563eb" }}>{s.qty}</td>
                        <td style={{ ...S.td, textAlign:"right" }}>{INR(s.revenue)}</td>
                        <td style={{ ...S.td, textAlign:"right", color:"#15803d", fontWeight:600 }}>{INR(s.sellerRevenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* State distribution */}
            <div>
              <div style={S.card}>
                <div style={S.hdr}><span>🗺 Orders by State</span></div>
                <div style={{ padding:14 }}>
                  {stateList.length===0 ? <div style={{ color:"#9ca3af", fontSize:12 }}>No data</div> :
                    stateList.map(([state, count]) => {
                      const max = stateList[0][1];
                      return (
                        <div key={state} style={{ marginBottom:8 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:3 }}>
                            <span style={{ fontWeight:600 }}>{state||"Unknown"}</span>
                            <span style={{ color:"#9ca3af" }}>{count} orders ({Math.round(count/delivered.length*100)}%)</span>
                          </div>
                          <div style={{ height:5, background:"#f3f4f6", borderRadius:3, overflow:"hidden" }}>
                            <div style={{ height:5, background:"#b5003c", width:(count/max*100)+"%", borderRadius:3 }}/>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              </div>

              <div style={S.card}>
                <div style={S.hdr}><span>↩️ Return Reasons</span></div>
                <div style={{ padding:14 }}>
                  {returns.length===0 ? <div style={{ color:"#9ca3af", fontSize:12 }}>No returns data</div> : (() => {
                    const rc = {};
                    returns.forEach(r=>{ if(r.returnReason) rc[r.returnReason]=(rc[r.returnReason]||0)+1; });
                    return Object.entries(rc).sort((a,b)=>b[1]-a[1]).map(([reason, count])=>(
                      <div key={reason} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid #f9fafb", fontSize:12 }}>
                        <span style={{ color:"#6b7280" }}>{reason}</span>
                        <span style={{ fontWeight:600, color:"#dc2626" }}>{count}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
